import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { resolveActionBlockMessage } from '../lib/action-denied.js';
import {
  requiresMultipleSignatories,
  MULTIPLE_SIGNATORIES_MESSAGE,
  signatoryBlockMessage,
} from '../lib/signatories.js';
import { EmailTemplateMenu } from '../email-templates/EmailTemplateMenu.js';
import { STANDING_ORDERS_TEMPLATES } from '../email-templates/templates.js';
import { accountOptionLabel } from '../lib/account-label.js';
import { useContentMap } from '../content.js';

/**
 * SCRN 28 — Bill Payment (Payee) Overview (CCF-64 listing + CCF-66 beneficiary
 * detail).
 *
 * Faithful port of the customer's Figma "Payments homescreen" design, scoped to
 * the realized slice: the agent picks the "Bill Payments" payment option, then
 * "Bill payment overview", then an account; the screen lists the account's bill
 * payments (Payment Type "BP") and, on View/Amend, shows the beneficiary detail
 * panel below the table. Hardcoded sample data from the export is replaced with
 * live data from the Teller-ID-gated BFF via the generated typed client:
 *   - accounts:  POST /api/customers/accounts
 *   - listing:   POST /api/payments/bill-list   (includeCancelled toggle)
 *   - detail:    POST /api/payments/bill
 *
 * Per the supervisor's authoritative UX refinement (#269 — the in-app
 * bug-reporter ticket, consistent with the sibling Standing Orders #267 and
 * Direct Debits #255): the agent CLICKS a listing row directly to open the
 * populated View/edit/cancel panel below the table (no per-row "View / Amend"
 * button), and each row carries a trailing trashcan that cancels the bill
 * payment. This intentionally supersedes CCF-64/68's older "enter via View/Amend
 * then tick Confirm all details" cancel entry-point; the confirmation itself is
 * NOT dropped — the trashcan opens a modal confirmation dialog whose
 * "Confirm all details" tick gates the live cancel exactly as the detail panel
 * does, so a state-changing banking action can never fire on a single click.
 * Both the trashcan dialog and the panel's Cancel route through the same
 * Teller-ID + RBAC + verification-gated /payments/bill-cancel op.
 *
 * The shared agent banner ("NavDark") lives in the app shell (AppLayout), so
 * this screen renders only the `<main>` content. The Bill-payment overview
 * (CCF-64/66) and Create payee (SCRN-29 / CCF-65 — the Add payee / VISA /
 * mortgage tabs) and Transfer-to-payee (SCRN-30 / CCF-100 — select an existing
 * payee, enter an amount, confirm the scripting + timeframe, complete the
 * transfer via POST /api/payments/payee) branches are built; "Standing orders"
 * routes to its own screen (SCRN-37), "Transfer between accounts" to Funds
 * Transfer (SCRN-32) and "Direct Debits" to the Direct Debits screen
 * (SCRN-33/34, issue #64).
 */

/** A single account row (open contract shape; subset the selector renders). */
interface AccountRow {
  AccountNumber: string;
  AccountType: string;
  Nickname?: string;
  Balance?: number;
  AvailableBalance?: number;
  Currency?: string;
  /**
   * #799 — the per-account signatory count the BFF normalises from the gateway
   * `customer/accounts/list` `NumofSignatories` field. When it is > 1 the payee
   * amend / create-payee / transfer-to-payee controls are suppressed ("two to
   * sign" mandate); read via the shared `requiresMultipleSignatories` helper.
   */
  Signatories?: number | string;
}

/** A bill-payment (payee) listing row — Payment Type "BP" (CCF-64). */
interface BillPaymentRow {
  PaymentId?: string;
  /** Payee record keys (CCF-100 transfer-to-payee identifies the payee by these). */
  PaymentsSeq?: string;
  PaymentsNo?: string;
  Name?: string;
  Reference?: string;
  Status?: string;
  Type?: string;
}

/** Beneficiary detail for a single bill payment (CCF-66). */
interface BillPaymentDetail {
  Found?: boolean;
  PaymentId?: string;
  Name?: string;
  Reference?: string;
  Status?: string;
  Type?: string;
  BeneficiaryIban?: string;
  BeneficiaryBic?: string;
  BeneficiaryName?: string;
  BeneficiaryReference?: string;
  RemitData?: string;
  PaymentsSeq?: string;
  PaymentsNo?: string;
}

/**
 * Client-side page size for the transfer-to-payee selection list (#462, CCF-64):
 * the list shows this many payees per screen with Next/Previous controls instead
 * of every payee on one page.
 */
const PAYEES_PER_PAGE = 10;

/**
 * Client-side page size for the bill-payments OVERVIEW list (#481 — "List
 * lengths": cap each page at 10 rows and let the agent step through with
 * Next/Previous). Pagination is client-side over the already-loaded list,
 * mirroring the Standing Orders / payee pattern.
 */
const BILL_PAYMENTS_PER_PAGE = 10;

/** Bill-payment lifecycle status → display label. */
const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  CANCELLED: 'Cancelled',
};

/**
 * True when a bill payment is still ACTIVE (its lifecycle `Status`, normalised
 * upper-case by the BFF adapter, is "ACTIVE"). Only active payments can be
 * cancelled, so the cancel controls — the per-row trashcan and the detail
 * panel's "Cancel bill payment" button — are shown ONLY when this is true.
 *
 * Issue #579 (CCF-68): once a bill payment is cancelled (visible via the "Show
 * cancelled" toggle, `Status` = CANCELLED, and likewise the other non-active
 * states LAPSED/COMPLETED the gateway's "Others" bucket can return), the cancel
 * controls "do not need to exist anymore" — an agent cannot re-cancel an
 * already-cancelled payment. Keep them for active payments only. A missing/blank
 * status is treated as NOT active (fail-closed — no cancel control shown).
 */
export function isBillPaymentActive(status?: string): boolean {
  return (status ?? '').trim().toUpperCase() === 'ACTIVE';
}

/**
 * Allowed characters for the editable beneficiary fields (CCF-67):
 * `a–z A–Z 0–9 / - ? : ( ) . , ' +` and Space.
 *
 * NB: CCF-67 originally also listed `€` for Remit Data, but the PTSB backend
 * (Unibanks) does not accept a euro sign — it aborts the gateway session with a
 * transport-level runtime error (issue #550). @dylan219 confirmed the character
 * was in the acceptance criteria in error, so `€` is blocked here (option B) to
 * stop an agent triggering the crash. Remit Data therefore uses the same set as
 * the other beneficiary fields — enforced again in the BFF (see api.ts).
 */
const BENEFICIARY_CHARS = /^[A-Za-z0-9/\-?:().,'+ ]*$/;
const REMIT_CHARS = BENEFICIARY_CHARS;

/**
 * Validates an editable beneficiary field against the CCF-67 rules; returns an
 * error message or '' when valid.
 */
function validateField(
  label: string,
  value: string,
  maxLen: number,
  required: boolean,
  isRemit = false,
): string {
  if (!value) return required ? `${label} is required.` : '';
  if (value.length > maxLen)
    return `${label} must be ${maxLen} characters or fewer.`;
  if (!(isRemit ? REMIT_CHARS : BENEFICIARY_CHARS).test(value))
    return `${label} contains invalid characters.`;
  return '';
}

/**
 * Documented `payment/bill-amend` `ErrorNumber` → its human-readable reason
 * (@dylan219, issue #190). The gateway returns the generic "Unable to Process
 * Transaction" for this decline, so the agent had no clue why the amend was
 * refused. Code **9287** means the beneficiary name cannot be changed for this
 * bill type — surface that plainly instead of the generic message. Mirrors the
 * `PAYEE_CREATE_CODE_REASON` pattern; undocumented codes fall through to the
 * verbatim gateway message.
 */
export const BILL_AMEND_CODE_REASON: Record<string, string> = {
  '9287': 'Unable to change beneficiary name for this bill type',
};

/**
 * Bill-payment types whose beneficiary name the gateway REFUSES to amend —
 * `payment/bill-amend` declines a name change on these with ErrorNumber **9287**
 * ("Beneficiary Name cannot be changed for this type"). The Unibanks
 * AmendBillPayment spec fixes the name for the Utility Bill and Hogan mortgage
 * bill types, and the pre-registered VISA biller behaves the same (#530: the
 * 14 greyed-out billers the customer listed — Electric Ireland, Vodafone,
 * permanent tsb VISA Card, permanent tsb finance, etc. — are all utility /
 * VISA / mortgage-type billers). The bill type arrives as `BillPaymentType` on
 * the `payment/bill-details` response (contract enum: "permanent tsb VISA" /
 * "PERMANENT TSB MORTGAGE" / "UTILITY BILL PAYMENT" / "OTHER PAYMENTS"), which
 * the BFF maps onto the detail's `Type`.
 *
 * Issue #530: rather than letting the agent attempt the edit and hit 9287, the
 * name field is GREYED OUT (non-editable) for these bill types. Any other or
 * missing type stays editable — the gateway backstops with 9287, mapped inline
 * via {@link BILL_AMEND_CODE_REASON}. Compared whitespace/case-insensitively.
 */
const NAME_LOCKED_BILL_TYPES: ReadonlySet<string> = new Set([
  'UTILITY BILL PAYMENT',
  'PERMANENT TSB VISA',
  'PERMANENT TSB MORTGAGE',
]);

/**
 * True when the beneficiary name cannot be amended for this bill-payment type
 * (#530) — the amend form greys the name field out for these billers.
 */
export function isBillNameLocked(billPaymentType?: string): boolean {
  const normalised = (billPaymentType ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();
  return NAME_LOCKED_BILL_TYPES.has(normalised);
}

/**
 * Format a failed bill-payment action (Amend/Cancel) for the INLINE error shown
 * beside the action button (authoritative @dylan219, issue #87): surface the
 * gateway's own Code + Message verbatim (e.g. "161 — Unable to Process
 * Transaction"), falling back to a clear retryable message. A `0000` code is
 * treated as no code. Mirrors PersonalDetailsScreen's `formatUpdateError` (#82).
 *
 * When a `codeReason` map is supplied and the numeric code is a KNOWN documented
 * `ErrorNumber` (#190), its human-readable reason replaces the generic gateway
 * message — e.g. "9287 — Unable to change beneficiary name for this bill type"
 * rather than "9287 — Unable to Process Transaction".
 */
export function formatActionError(
  result: { ResultCode?: string; ResultMessage?: string } | undefined,
  fallback: string,
  codeReason?: Record<string, string>,
): string {
  const code = result?.ResultCode?.trim();
  const message = result?.ResultMessage?.trim();
  const showCode = code && code !== '0000';
  const reason = showCode ? codeReason?.[code] : undefined;
  if (showCode && reason) return `${code} — ${reason}`;
  if (showCode && message) return `${code} — ${message}`;
  if (message) return message;
  if (showCode) return `${fallback} (code ${code}).`;
  return fallback;
}

/** Accessible dropdown, faithful to the export's listbox pattern. */
function Dropdown({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor={id}
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <div className="relative">
        <button
          id={id}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span className={selected ? '' : 'text-[#6b7280]'}>
            {selected ? selected.label : placeholder}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            aria-label={label}
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((option) => (
              <li
                key={option.value || 'placeholder'}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(option.value);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === option.value ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Accessible toggle switch, faithful to the export's "Show cancelled" toggle. */
function Toggle({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: () => void;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
        className="sr-only peer"
        aria-checked={checked}
      />
      <label
        htmlFor={id}
        className="relative flex items-center cursor-pointer group"
      >
        <div
          className={`relative w-[44px] h-[24px] rounded-full transition-all ${
            checked ? 'bg-[#FF703E]' : 'bg-[#d1d5db]'
          } peer-focus-visible:outline peer-focus-visible:outline-4 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#005fcc]`}
        >
          <div
            className={`absolute top-[2px] left-[2px] w-[20px] h-[20px] rounded-full bg-white transition-transform ${
              checked ? 'translate-x-[20px]' : 'translate-x-0'
            }`}
          ></div>
        </div>
        <span className="ml-3 text-[14px] font-['Arial',sans-serif] text-[#6b7280] select-none">
          {label}
        </span>
      </label>
    </div>
  );
}

/** A labelled text input with an inline error slot (Create payee forms). */
function PayeeInput({
  id,
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  error,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  error?: string;
  hint?: string;
}): React.JSX.Element {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-2"
      >
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-invalid={!!error}
        className={`w-full px-4 py-2 border rounded text-[14px] font-['Arial',sans-serif] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:border-[#005fcc] ${
          error ? 'border-[#dc2626]' : 'border-[#979c9f]'
        }`}
      />
      {hint && !error && (
        <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mt-2 m-0">
          {hint}
        </p>
      )}
      <div className="min-h-[18px]">
        {error && (
          <p
            className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Create payee (SCRN 29 / CCF-65) — the three tabs (Add payee / Add PTSB Visa
 * Credit Card / Add PTSB mortgage) and their forms, posting to
 * `/payments/bill-create`. Self-contained: own field + submit state, reset when
 * the account changes (the parent keys it by account).
 */
/**
 * Known create-payee / bill-payment `ErrorNumber` → its documented,
 * human-readable reason (#184). The gateway's own message is the generic
 * "Unable to Process Transaction" for every decline, so the agent only ever saw
 * e.g. "941 — Unable to Process Transaction" with no clue why; the reason is
 * what they actually need. Mirrors `FundsTransferScreen.TRANSFER_CODE_REASON`
 * and `StatementsScreen.STATEMENT_ORDER_CODE_REASON`.
 *
 * Issue #184: a tester saw "941 — Unable to Process Transaction" on an account
 * with 3 holds and assumed the holds (or the IBAN) were the cause. Code 941 is
 * in fact an **account-type restriction** ("Bill payment not allowed on this
 * account type"), not a holds rule and not an IBAN problem — which is why the
 * same IBAN worked on a different (permitted-type) account. Surfacing the
 * documented reason makes that unambiguous.
 *
 * Sources (the authoritative Unibanks references; only documented codes/wording
 * are mapped — undocumented codes fall through to the verbatim gateway message):
 *   - requirements/unibanks-reference/Unibanks_CreateABill.yaml (the create-payee
 *     op — IBAN/VISA/payment-type validation)
 *   - requirements/unibanks-reference/Unibanks_BillPayment.yaml (account-type +
 *     limit decline codes)
 */
const PAYEE_CREATE_CODE_REASON: Record<string, string> = {
  // CreateABill validation
  '100': 'Account not found.',
  '599': 'Invalid IBAN entered.',
  '9103': 'Invalid card number.',
  '9153': 'Invalid Visa Number', // #268 — align wording across SO + bill-payment flows
  '9149': 'This payment type has been disabled.',
  // BillPayment account-type restrictions (the #184 case)
  '941': 'Bill payment not allowed on this account type.',
  '942': 'Account type not allowed as the paying account.',
  '757': 'Transaction not allowed for this account type.',
  // BillPayment limit declines
  '145': 'Utility daily limit or number of payments exceeded.',
  '144': 'Utility bill payments limit exceeded.',
  '146': 'Visa transfer daily limit or number of payments exceeded.',
};

/**
 * The subset of decline codes that genuinely point at the IBAN the agent
 * entered (#184) — only these warrant the IBAN-specific "re-confirm the IBAN"
 * recovery hint. Any other decline (e.g. the account-type restriction 941) gets
 * a neutral hint so the agent is not misdirected to re-check a correct IBAN.
 */
export const PAYEE_IBAN_DECLINE_CODES = new Set(['599']);

/** True when a create-payee decline code points at the entered IBAN (#184). */
export function isIbanDeclineCode(code: string | undefined): boolean {
  return code !== undefined && PAYEE_IBAN_DECLINE_CODES.has(code.trim());
}

/**
 * Format the gateway outcome of a create-payee write into a single inline line
 * for the agent (#121 — authoritative inline-error UX, @dylan219 #80/#82/#94):
 * surface the gateway error **Code + Message** beside the action button so the
 * agent sees the real decline and can retry. The BFF returns BOTH a flat
 * `{ResultCode, ResultMessage}` (2xx Success:false) and a `{gateway:{code,message}}`
 * envelope (transport 4xx); read either.
 *
 * When the numeric code is a KNOWN documented `ErrorNumber` (#184) we append the
 * reference's human-readable reason instead of the generic gateway message —
 * e.g. "941 — Bill payment not allowed on this account type." rather than
 * "941 — Unable to Process Transaction" — so the agent isn't left guessing.
 * `0000` is the internal success code and is never shown. Falls back to the
 * verbatim gateway message, then a generic line, when no reason is mapped.
 */
export function formatPayeeCreateError(body: unknown): string {
  const b = (body ?? {}) as {
    ResultCode?: string;
    ResultMessage?: string;
    gateway?: { code?: string | number; message?: string };
  };
  const rawCode = b.gateway?.code ?? b.ResultCode;
  const code = rawCode === undefined ? '' : String(rawCode).trim();
  const message = (b.gateway?.message ?? b.ResultMessage)?.trim();
  const showCode = code && code !== '0000';
  const reason = showCode ? PAYEE_CREATE_CODE_REASON[code] : undefined;
  if (showCode && reason) return `${code} — ${reason}`;
  if (showCode && message) return `${code} — ${message}`;
  if (message) return message;
  if (showCode) return `Could not create the payee (code ${code}).`;
  return 'Could not create the payee. Please retry.';
}

/** Extract the gateway decline code from a create-payee outcome body (#184). */
export function payeeCreateErrorCode(body: unknown): string | undefined {
  const b = (body ?? {}) as {
    ResultCode?: string;
    gateway?: { code?: string | number };
  };
  const rawCode = b.gateway?.code ?? b.ResultCode;
  return rawCode === undefined ? undefined : String(rawCode).trim();
}

function CreatePayeeSection({
  customerNumber,
  tellerId,
  accountNumber,
  blockedBySignatories,
}: {
  customerNumber: string;
  tellerId: string;
  accountNumber: string;
  /**
   * #799 increment 3 — true when the selected account requires MORE THAN ONE
   * signatory ("two to sign" mandate): the Complete submit is disabled and the
   * verbatim block message shows inline beside it. The BFF `/payments/bill-create`
   * route enforces the SAME block server-side (#173).
   */
  blockedBySignatories: boolean;
}): React.JSX.Element {
  const [tab, setTab] = useState<'' | 'payee' | 'visa' | 'mortgage'>('');
  const [iban, setIban] = useState('');
  const [name, setName] = useState('');
  const [reference, setReference] = useState('');
  const [remit, setRemit] = useState('');
  const [visaCard, setVisaCard] = useState('');
  const [visaName, setVisaName] = useState('');
  const [mortgageIban, setMortgageIban] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // #121: the gateway decline (Code + Message) shown INLINE beside the Complete
  // button as a recoverable warning so the agent can re-confirm the details with
  // the customer and retry — not a top-right toast that disappears.
  const [submitError, setSubmitError] = useState('');
  // #184: the gateway decline code drives whether the recovery hint is the
  // IBAN-specific one (only for IBAN-validation declines) or a neutral retry
  // hint, so an account-type restriction (941) is not mislabelled as an IBAN
  // problem. Empty = no decline shown.
  const [submitErrorCode, setSubmitErrorCode] = useState('');

  function selectTab(next: 'payee' | 'visa' | 'mortgage'): void {
    setTab(next);
    setConfirm(false);
    setErrors({});
    setDone(false);
    setSubmitError('');
    setSubmitErrorCode('');
  }

  function validateTab(): Record<string, string> {
    const e: Record<string, string> = {};
    if (tab === 'payee') {
      const v = iban.replace(/\s+/g, '');
      if (!v) e.iban = 'IBAN is required.';
      else if (!/^[A-Za-z]{2}\d{2}[A-Za-z0-9]{11,30}$/.test(v))
        e.iban = 'Enter a valid IBAN.';
      const n = validateField('Beneficiary name', name, 18, true);
      if (n) e.name = n;
      const r = validateField('Reference', reference, 18, true);
      if (r) e.reference = r;
      const rd = validateField('Remit data', remit, 40, false, true);
      if (rd) e.remit = rd;
    } else if (tab === 'visa') {
      if (!/^\d{16}$/.test(visaCard.replace(/\s+/g, '')))
        e.visaCard = 'Enter a valid 16-digit VISA card number.';
      const n = validateField('Name', visaName, 18, true);
      if (n) e.visaName = n;
    } else if (tab === 'mortgage') {
      const v = mortgageIban.replace(/\s+/g, '');
      if (!v) e.mortgageIban = 'PTSB mortgage IBAN is required.';
      else if (!/^[A-Za-z]{2}\d{2}[A-Za-z0-9]{11,30}$/.test(v))
        e.mortgageIban = 'Enter a valid IBAN.';
    }
    return e;
  }

  async function submit(): Promise<void> {
    if (!tab) return;
    // #799 increment 3 — defence in depth: the Complete submit is already
    // disabled on a >1-signatory account, but re-assert the block here so the
    // create op can never fire for a "two to sign" account. The server enforces
    // the same block (#173).
    if (blockedBySignatories) {
      setSubmitError(MULTIPLE_SIGNATORIES_MESSAGE);
      setSubmitErrorCode('');
      toast.error(MULTIPLE_SIGNATORIES_MESSAGE);
      return;
    }
    const e = validateTab();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setSubmitError('');
    setSubmitErrorCode('');
    setSubmitting(true);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/bill-create', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber,
          payeeType: tab,
          ...(tab === 'payee'
            ? {
                iban: iban.replace(/\s+/g, ''),
                beneficiaryName: name,
                reference,
                ...(remit ? { remitData: remit } : {}),
              }
            : {}),
          ...(tab === 'visa'
            ? { visaCardNumber: visaCard.replace(/\s+/g, ''), visaName }
            : {}),
          ...(tab === 'mortgage'
            ? { mortgageIban: mortgageIban.replace(/\s+/g, '') }
            : {}),
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Issue #248 — the RBAC *action* denial: `requirePermission` refuses a
        // blocked write (createBillPayment) with 403 `{ error: 'forbidden' }`.
        // Surface @dylan219's authoritative verbatim copy inline beside the
        // Complete button — never a raw 403 or a generic string. Distinct from
        // the gateway business-decline (`formatPayeeCreateError`) and from the
        // Teller-ID gate (`invalid_teller_id`, a different 403 body that
        // `resolveActionBlockMessage` deliberately ignores, keeping its own message).
        // #799 increment 3: the server-side multi-signatory block
        // (`ResultCode: 'MULTIPLE_SIGNATORIES'`, the gateway never called) shows
        // the verbatim advisory verbatim — no code prefix (mirrors the
        // THREE_HOLD handling on ChequebookScreen).
        const message =
          resolveActionBlockMessage(response.status, apiError) ??
          signatoryBlockMessage(
            (data ?? {}) as { ResultCode?: string; ResultMessage?: string },
          ) ??
          (response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : formatPayeeCreateError(apiError ?? data));
        // Inline warning beside the Complete button (#121) AND a toast so the
        // failure is unmissable; the inline line persists so the agent can
        // re-confirm the details with the customer and retry. #184: capture the
        // decline code so the recovery hint can be IBAN-specific only for IBAN
        // declines (not for an account-type restriction like 941).
        setSubmitError(message);
        setSubmitErrorCode(
          response.status === 403
            ? ''
            : (payeeCreateErrorCode(apiError ?? data) ?? ''),
        );
        toast.error(message);
        return;
      }
      setSubmitError('');
      setSubmitErrorCode('');
      setDone(true);
      const label =
        tab === 'visa'
          ? 'Visa card added'
          : tab === 'mortgage'
            ? 'PTSB mortgage added'
            : 'Payee added';
      toast.success(`${label} successfully.`);
    } catch {
      const message = 'Could not create the payee. Please retry.';
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  const tabButton = (
    key: 'payee' | 'visa' | 'mortgage',
    text: string,
  ): React.JSX.Element => (
    <button
      type="button"
      onClick={() => selectTab(key)}
      className={`px-6 py-3 rounded-md text-[16px] font-['Arial',sans-serif] font-bold focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all ${
        tab === key
          ? 'bg-[#FF703E] text-[#1D252C]'
          : 'bg-white text-[#FF703E] border-2 border-[#FF703E] hover:bg-[#fff5f2]'
      }`}
    >
      {text}
    </button>
  );

  return (
    <div className="mt-2">
      <div className="flex gap-4 flex-wrap mb-2">
        {tabButton('payee', 'Add payee')}
        {tabButton('visa', 'Add PTSB Visa Credit Card')}
        {tabButton('mortgage', 'Add PTSB mortgage')}
      </div>

      {tab && (
        <div className="mt-8 p-6 border-2 border-[#dad7d3] bg-white max-w-[500px]">
          <h3 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6">
            {tab === 'visa'
              ? 'Add PTSB Visa Credit Card'
              : tab === 'mortgage'
                ? 'Add PTSB mortgage'
                : 'Add payee'}
          </h3>

          {done ? (
            <p
              className="text-[16px] font-['Arial',sans-serif] font-bold text-[#16a34a] m-0"
              role="alert"
              aria-live="polite"
            >
              {tab === 'visa'
                ? 'Visa card added'
                : tab === 'mortgage'
                  ? 'PTSB mortgage added'
                  : 'Payee added'}
            </p>
          ) : (
            <div className="space-y-4">
              {tab === 'payee' && (
                <>
                  <PayeeInput
                    id="add-payee-iban"
                    label="IBAN"
                    value={iban}
                    onChange={setIban}
                    placeholder="IBAN"
                    error={errors.iban}
                  />
                  <PayeeInput
                    id="add-payee-name"
                    label="Beneficiary name"
                    value={name}
                    onChange={setName}
                    placeholder="Beneficiary Name"
                    maxLength={18}
                    error={errors.name}
                    hint="For verification of payee purposes, confirm with the customer that this name matches the name on the beneficiary account."
                  />
                  <PayeeInput
                    id="add-payee-reference"
                    label="Reference (max 18 characters)"
                    value={reference}
                    onChange={setReference}
                    placeholder="Reference"
                    maxLength={18}
                    error={errors.reference}
                  />
                  <PayeeInput
                    id="add-payee-remit"
                    label="Remit Data"
                    value={remit}
                    onChange={setRemit}
                    placeholder="Remit Data"
                    maxLength={40}
                    error={errors.remit}
                  />
                </>
              )}
              {tab === 'visa' && (
                <>
                  <PayeeInput
                    id="visa-card-number"
                    label="Visa Credit Card number"
                    value={visaCard}
                    onChange={setVisaCard}
                    placeholder="Visa Credit Card number"
                    maxLength={16}
                    error={errors.visaCard}
                  />
                  <PayeeInput
                    id="visa-card-name"
                    label="Name"
                    value={visaName}
                    onChange={setVisaName}
                    placeholder="Name"
                    maxLength={18}
                    error={errors.visaName}
                  />
                </>
              )}
              {tab === 'mortgage' && (
                <PayeeInput
                  id="mortgage-iban"
                  label="PTSB Mortgage IBAN"
                  value={mortgageIban}
                  onChange={setMortgageIban}
                  placeholder="PTSB Mortgage IBAN"
                  error={errors.mortgageIban}
                />
              )}

              <div className="flex items-start gap-3 pt-2">
                <input
                  type="checkbox"
                  id="create-payee-confirm"
                  checked={confirm}
                  onChange={() => setConfirm((c) => !c)}
                  className="size-[20px] accent-[#FF703E] cursor-pointer mt-[2px]"
                />
                <label
                  htmlFor="create-payee-confirm"
                  className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] leading-relaxed cursor-pointer"
                >
                  Confirm above details before proceeding
                </label>
              </div>

              {confirm && (
                <button
                  type="button"
                  disabled={submitting || blockedBySignatories}
                  title={
                    blockedBySignatories
                      ? MULTIPLE_SIGNATORIES_MESSAGE
                      : undefined
                  }
                  onClick={() => void submit()}
                  className="mt-2 px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Working…' : 'Complete'}
                </button>
              )}

              {/* #799 increment 3 (@dylan219): the selected account requires
                  more than one signatory ("two to sign" mandate), so setting up
                  a payee is NOT permitted through the desktop. The verbatim
                  block message shows inline beside the Complete button (which
                  is disabled). The agent can select a different account.
                  Enforced server-side on /payments/bill-create too, so a reload
                  / direct API call cannot bypass it (#173). */}
              {blockedBySignatories && (
                <div
                  role="alert"
                  aria-live="polite"
                  className="mt-2 max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                >
                  {MULTIPLE_SIGNATORIES_MESSAGE}
                </div>
              )}

              {submitError && (
                <div
                  id="create-payee-error"
                  role="alert"
                  aria-live="assertive"
                  className="mt-2 max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                >
                  <p className="m-0 font-bold">{submitError}</p>
                  <p className="m-0 mt-1 text-[#6b7280]">
                    {isIbanDeclineCode(submitErrorCode)
                      ? 'Please re-confirm the IBAN with the customer and try again.'
                      : 'Confirm the details with the customer and retry.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Known ThirdPartyPayment (transfer-to-payee, CCF-100) ErrorNumber → its
 * documented, human-readable reason. Source: the authoritative
 * `requirements/unibanks-reference/Unibanks_ThirdPartyPayment.yaml` (the
 * operation behind `POST /payments/payee`) — using the reference's own
 * `summary` wording. The gateway returns the generic "Unable to Process
 * Transaction" for every decline, so the numeric ErrorNumber is the only signal
 * of the real reason; this map turns it into something the agent can act on.
 *
 * #183/#167: code **145 is "Utility Daily Limit Exceeded", NOT insufficient
 * funds** — a tester mislabelled it (exactly the #167 trap, where 143 was
 * reported as "insufficient funds" but actually means "Daily Transfer Limit
 * exceeded"). Only documented codes are mapped; an undocumented code falls back
 * to the gateway's verbatim message (no guessing at meanings).
 */
const THIRD_PARTY_TRANSFER_CODE_REASON: Record<string, string> = {
  '145': 'Utility daily limit exceeded.',
  '144': 'Utility bill payments limit exceeded.',
  '146': 'Visa transfer daily limit or number exceeded.',
  '147': 'Transaction amount not acceptable.',
  '154': 'Channel is closed.',
  '153': 'Invalid combination of key elements.',
  '253': 'Bill payment not active.',
  '315': 'Invalid recipient service.',
  '934': 'Invalid date.',
  '991': 'Future-dated payments limit exceeded.',
  '990': 'Future-dated payments not permitted.',
  '514': 'Non-SEPA bill payment restricted.',
  '661': 'SEPA bill payment restricted.',
  '941': 'Bill payment not allowed on this account type.',
  '942': 'Account type not allowed as the paying account.',
  '757': 'Transaction not allowed for this account type.',
  '105': 'The transfer-to account is not available.',
  '106': 'The transfer-from account is not available.',
  '100': 'Account not found.',
  '200': 'The account is dormant.',
  '110': 'Transaction not allowed on a foreign-exchange account.',
  '299': 'Insufficient funds — transaction cancelled.',
  '302': 'Insufficient funds — a 090 override is required.',
  '416': 'More than one signature is required for this payment.',
  '232': 'Withdrawal exceeds the limit.',
  '972': 'Account hold not set up.',
  // #286 (CCF-100 unhappy path #2 — "Hold on account preventing transfer"):
  // ErrorNumber 917 is the "Pay No Withdrawal" hold (its `summary` in
  // Unibanks_ThirdPartyPayment.yaml, matching the "N — Pay No Withdrawal" entry
  // in the DEFINED_HOLDS catalogue). Previously unmapped, so the agent saw the
  // bare generic "917 — Unable to process Transaction" with no indication a
  // hold was the cause.
  '917':
    'A "Pay No Withdrawal" hold on the account is preventing the transfer.',
};

/**
 * Format the gateway outcome of a transfer-to-payee write into a single inline
 * line for the agent (#151/#152 — authoritative inline-error UX, @dylan219
 * #80/#82/#94): surface the gateway error **Code + Message verbatim** beside the
 * Complete Transfer button so the agent can re-check the amount/details with the
 * customer and retry — NOT a top-right toast that disappears. The BFF returns
 * BOTH a flat `{ResultCode, ResultMessage}` (2xx Success:false, `mapWriteOutcome`
 * of a gateway `SuccessErrorInd:'N'`) AND a `{gateway:{code,message}}` envelope
 * (transport 4xx); read either. The message text is the gateway's own (e.g.
 * "The fields PaymentsNo must be between 51 and 999") and is shown verbatim.
 * `0000` is the internal success code and is never shown. Falls back to a
 * generic line only when the gateway gave neither (e.g. a transport failure).
 * Mirrors `formatPayeeCreateError`/`FundsTransferScreen.formatTransferError`.
 */
export function formatPayeeTransferError(body: unknown): string {
  const b = (body ?? {}) as {
    ResultCode?: string;
    ResultMessage?: string;
    gateway?: { code?: string | number; message?: string };
  };
  const rawCode = b.gateway?.code ?? b.ResultCode;
  const code = rawCode === undefined ? '' : String(rawCode).trim();
  const message = (b.gateway?.message ?? b.ResultMessage)?.trim();
  const showCode = code && code !== '0000';
  // Known ThirdPartyPayment ErrorNumber → its documented reason (#183/#167).
  // The gateway's own message is the generic "Unable to Process Transaction" for
  // every code, so the mapped reason is what the agent actually needs (e.g. 145
  // = "Utility daily limit exceeded.", NOT insufficient funds).
  const reason = showCode ? THIRD_PARTY_TRANSFER_CODE_REASON[code] : undefined;
  if (showCode && reason) return `${code} — ${reason}`;
  if (showCode && message) return `${code} — ${message}`;
  if (message) return message;
  if (showCode) return `Could not complete the transfer (code ${code}).`;
  return 'Could not complete the transfer. Please retry.';
}

/**
 * Transfer to payee (SCRN 30 / CCF-100) — lists the account's existing payees
 * (the bill-payment listing, reused), the agent selects one, enters an amount,
 * confirms the scripting + payment timeframe, then completes the transfer via
 * `/payments/payee`. Self-contained: own selection + submit state, reset when
 * the account changes (the parent keys it by account).
 */
function TransferToPayeeSection({
  customerNumber,
  tellerId,
  accountNumber,
  blockedBySignatories,
}: {
  customerNumber: string;
  tellerId: string;
  accountNumber: string;
  /**
   * #799 increment 3 — true when the FUNDING account requires MORE THAN ONE
   * signatory ("two to sign" mandate): the Complete Transfer submit is disabled
   * and the verbatim block message shows inline beside it. The BFF
   * `/payments/payee` route enforces the SAME block server-side (#173).
   */
  blockedBySignatories: boolean;
}): React.JSX.Element {
  const [payees, setPayees] = useState<BillPaymentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // #462 (CCF-64): the payee list is paged — PAYEES_PER_PAGE rows per screen with
  // Next/Previous, rather than listing every payee on one page. Zero-based page
  // index; reset to the first page whenever the account changes.
  const [page, setPage] = useState(0);
  // #829 (server-side pagination): the gateway returns at most ~100 payees per
  // call. `hasMore` mirrors the response `hasMore` for the CURRENTLY-loaded
  // block; the cursor is the continuation to fetch the NEXT ~100. When the agent
  // steps to a UI page whose rows aren't loaded yet AND `hasMore`, we lazily
  // re-call bill-list with the cursor and APPEND the next block (page 11 → 2nd
  // call, page 21 → 3rd, …). So Previous/Next can walk past page 10 when there
  // are >100 records, without a jump-to-page control (@dylan219 2026-09-24).
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const cursorRef = useRef<{ seq?: string; no?: string }>({});
  const [showSelected, setShowSelected] = useState(false);
  const [amount, setAmount] = useState('');
  const [amountConfirmed, setAmountConfirmed] = useState(false);
  const [timeframe, setTimeframe] = useState('');
  const [scriptingConfirmed, setScriptingConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // #151/#152: the gateway decline (Code + Message) — and the pre-submit client
  // guards — shown INLINE beside the Complete Transfer button as a recoverable
  // warning so the agent can re-check the amount/details with the customer and
  // retry, not only a top-right toast that disappears (authoritative @dylan219
  // #80/#82/#94 — ALL errors appear beside the button).
  const [submitError, setSubmitError] = useState('');
  // #304: true only when `submitError` holds the RBAC action-denial copy (#248
  // — `isActionForbidden`/`ACTION_DENIED_MESSAGE`), so the render below can
  // withhold the "re-check the amount/details" recovery hint — that hint is
  // for a gateway business decline and doesn't apply to a permission denial.
  const [submitForbidden, setSubmitForbidden] = useState(false);
  // #485: the Figma "Payee details" panel shows the beneficiary BIC + IBAN,
  // which are NOT on the listing row (`selected`). Fetch them from the
  // `bill-details` op when the agent clicks "Transfer to Selected" and hold
  // them in local state. A request-sequence guard drops a stale response so a
  // rapid re-select can't render a prior payee's IBAN/BIC; both reset to null
  // (rendered as "—") on a new selection and whenever the detail fetch fails,
  // so a detail-fetch failure never blocks the transfer flow.
  const [beneficiaryBic, setBeneficiaryBic] = useState<string | null>(null);
  const [beneficiaryIban, setBeneficiaryIban] = useState<string | null>(null);
  const detailSeq = useRef(0);

  // Load the account's existing bill-payment payees (CCF-100 payee selection).
  // First page only — the next ~100-row block is fetched lazily by `loadMore`
  // when the agent steps past the loaded rows (#829).
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setLoadError('');
      cursorRef.current = {};
      setHasMore(false);
      try {
        const {
          data,
          error: apiError,
          response,
        } = await api.POST('/payments/bill-list', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber, accountNumber },
        });
        if (cancelled) return;
        if (apiError || !data) {
          // CCF-100 unhappy path 3 — backend error returning the payee list.
          setLoadError(
            response.status === 403
              ? 'Forbidden: check your Teller ID and permissions.'
              : 'Could not load payees. Please retry.',
          );
          return;
        }
        setPayees((data.Payments ?? []) as unknown as BillPaymentRow[]);
        setHasMore(data.hasMore ?? false);
        cursorRef.current = {
          seq: data.nextPaymentsSeq,
          no: data.nextPaymentsNo,
        };
        setPage(0);
      } catch {
        if (!cancelled) setLoadError('Could not load payees. Please retry.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId, accountNumber]);

  /**
   * #829: fetch the NEXT ~100-payee block from the gateway using the
   * continuation cursor and APPEND it to the loaded rows. Called when the agent
   * steps to a UI page whose rows aren't loaded yet and `hasMore` is true. Guards
   * against a concurrent fetch (`loadingMore`) and a missing cursor. On failure
   * the loaded rows are left intact and `hasMore` is cleared so "Next" settles on
   * the last loaded page rather than retrying forever; the agent can retry by
   * re-selecting the account.
   */
  async function loadMore(): Promise<boolean> {
    if (loadingMore || !hasMore) return false;
    const cursor = cursorRef.current;
    if (!cursor.seq && !cursor.no) return false;
    setLoadingMore(true);
    try {
      const { data, error: apiError } = await api.POST('/payments/bill-list', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber,
          ...(cursor.seq ? { nextPaymentsSeq: cursor.seq } : {}),
          ...(cursor.no ? { nextPaymentsNo: cursor.no } : {}),
        },
      });
      if (apiError || !data) {
        setHasMore(false);
        return false;
      }
      const more = (data.Payments ?? []) as unknown as BillPaymentRow[];
      setPayees((cur) => [...cur, ...more]);
      setHasMore(data.hasMore ?? false);
      cursorRef.current = {
        seq: data.nextPaymentsSeq,
        no: data.nextPaymentsNo,
      };
      return more.length > 0;
    } catch {
      setHasMore(false);
      return false;
    } finally {
      setLoadingMore(false);
    }
  }

  /**
   * Step to the next UI page. If the next page's rows aren't loaded yet and the
   * gateway signals more, fetch the next ~100-row block first, then advance
   * (#829). "Next" is disabled only on the last loaded page when `hasMore` is
   * false (handled in the render).
   */
  async function goToNextPage(): Promise<void> {
    const nextStart = (page + 1) * PAYEES_PER_PAGE;
    if (nextStart >= payees.length && hasMore) {
      await loadMore();
    }
    setPage((p) => p + 1);
  }

  const selected = payees.find((p) => p.PaymentId === selectedId) ?? null;

  // Pagination (#462 client windowing + #829 server pagination): render only the
  // current page's slice of PAYEES_PER_PAGE rows over the LOADED rows. More UI
  // pages may exist beyond the loaded rows when the gateway signals `hasMore`, so
  // the page count is the greater of the loaded-row page count and "one more page
  // available". Clamp the index defensively.
  const loadedPageCount = Math.max(
    1,
    Math.ceil(payees.length / PAYEES_PER_PAGE),
  );
  const currentPage = Math.min(page, loadedPageCount - 1 + (hasMore ? 1 : 0));
  const pageStart = currentPage * PAYEES_PER_PAGE;
  const visiblePayees = payees.slice(pageStart, pageStart + PAYEES_PER_PAGE);
  // "Next" is enabled while there is another LOADED page OR the gateway signals
  // more rows to fetch. Disabled only on the last loaded page with no more.
  const hasNextPage = currentPage < loadedPageCount - 1 || hasMore;

  function viewSelected(): void {
    setShowSelected(true);
    setAmountConfirmed(false);
    setScriptingConfirmed(false);
    setTimeframe('');
    setDone(false);
    setSubmitError('');
    setSubmitForbidden(false);
    void loadBeneficiaryDetail();
  }

  /**
   * #485: fetch the selected payee's beneficiary BIC + IBAN from the
   * `bill-details` op (same request shape as the amend panel's `openPayment`)
   * for display in the "Payee details" panel. Fail-soft — on any error the
   * fields stay null and render "—" without blocking the transfer flow. A
   * request-sequence guard drops a stale response if a newer selection races
   * in while this one is in flight.
   */
  async function loadBeneficiaryDetail(): Promise<void> {
    const payee = selected;
    const seq = ++detailSeq.current;
    setBeneficiaryBic(null);
    setBeneficiaryIban(null);
    if (!payee) return;
    try {
      const { data, error: apiError } = await api.POST('/payments/bill', {
        params: { header: { 'X-Teller-Id': tellerId } },
        // Keyed on PaymentsSeq + PaymentsNo from the listing row (paymentId is
        // an id fallback) — mirrors the amend panel's `openPayment`.
        body: {
          customerNumber,
          accountNumber,
          paymentId: payee.PaymentId ?? '',
          ...(payee.PaymentsSeq ? { paymentsSeq: payee.PaymentsSeq } : {}),
          ...(payee.PaymentsNo ? { paymentsNo: payee.PaymentsNo } : {}),
        },
      });
      // A newer selection superseded this one — drop the stale response.
      if (seq !== detailSeq.current) return;
      if (apiError || !data || (data as { Found?: boolean }).Found === false) {
        return;
      }
      const detail = data as unknown as BillPaymentDetail;
      setBeneficiaryBic(detail.BeneficiaryBic ?? null);
      setBeneficiaryIban(detail.BeneficiaryIban ?? null);
    } catch {
      // Fail-soft: leave BIC/IBAN null → rendered as "—".
    }
  }

  async function completeTransfer(): Promise<void> {
    // #799 increment 3 — defence in depth: the Complete Transfer submit is
    // already disabled on a >1-signatory account, but re-assert the block here
    // so the transfer op can never fire for a "two to sign" account. The server
    // enforces the same block (#173).
    if (blockedBySignatories) {
      setSubmitError(MULTIPLE_SIGNATORIES_MESSAGE);
      setSubmitForbidden(false);
      toast.error(MULTIPLE_SIGNATORIES_MESSAGE);
      return;
    }
    // Route the pre-submit client guards to the SAME inline region as the
    // gateway decline (#94 — ALL errors appear beside the button), in addition
    // to the toast.
    if (!selected?.PaymentsSeq || !selected.PaymentsNo) {
      const message = 'This payee cannot be paid (missing reference).';
      setSubmitError(message);
      setSubmitForbidden(false);
      toast.error(message);
      return;
    }
    if (!/^(?!0+(\.0+)?$)\d+(\.\d{1,2})?$/.test(amount.trim())) {
      const message = 'Enter a valid transfer amount.';
      setSubmitError(message);
      setSubmitForbidden(false);
      toast.error(message);
      return;
    }
    if (!timeframe) {
      // CCF-100 — the agent must confirm the payment timeframe with the
      // customer before the transfer can be completed.
      const message = 'Confirm the payment timeframe with the customer first.';
      setSubmitError(message);
      setSubmitForbidden(false);
      toast.error(message);
      return;
    }
    setSubmitError('');
    setSubmitForbidden(false);
    setSubmitting(true);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/payee', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber,
          paymentsSeq: selected.PaymentsSeq,
          paymentsNo: selected.PaymentsNo,
          amount: amount.trim(),
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Inline warning beside the Complete Transfer button (#151/#152) AND a
        // toast so the failure is unmissable; the inline line persists so the
        // agent can re-check the amount/details with the customer and retry.
        // #152's message text (e.g. "The fields PaymentsNo must be between 51
        // and 999") is the real gateway's own validation response, surfaced
        // verbatim. Issue #248/#304: an RBAC action denial (transferToPayee,
        // e.g. blocked OFFLINE) refuses with 403 `{ error: 'forbidden' }` —
        // surface @dylan219's verbatim copy inline (mirrors the Create/Amend/
        // Cancel bill-payment actions on this same screen), never a raw 403 and
        // never with the gateway-decline "re-check the amount/details" hint,
        // which does not apply to a permission denial. Distinct from the
        // Teller-ID gate (`invalid_teller_id`, a different 403 body
        // `resolveActionBlockMessage` deliberately ignores, keeping its own message).
        // #799 increment 3: the server-side multi-signatory block
        // (`ResultCode: 'MULTIPLE_SIGNATORIES'`, the gateway never called) shows
        // the verbatim advisory — no code prefix and no "re-check the amount"
        // hint (the block is not a gateway business decline).
        const blocked = resolveActionBlockMessage(response.status, apiError);
        const signatory = signatoryBlockMessage(
          (data ?? {}) as { ResultCode?: string; ResultMessage?: string },
        );
        const message =
          blocked ??
          signatory ??
          (response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : formatPayeeTransferError(apiError ?? data));
        setSubmitError(message);
        setSubmitForbidden(blocked !== null);
        toast.error(message);
        return;
      }
      setSubmitError('');
      setSubmitForbidden(false);
      setDone(true);
      toast.success('Transfer to payee completed successfully.');
    } catch {
      const message = 'Could not complete the transfer. Please retry.';
      setSubmitError(message);
      setSubmitForbidden(false);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mt-2">
      {loadError && (
        <div
          role="alert"
          className="mb-6 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {loadError}
        </div>
      )}

      {/* Payee selection table (CCF-100). */}
      <div className="overflow-x-auto max-w-[900px] border border-[#dad7d3] bg-white">
        <table className="w-full border-collapse" role="table">
          <thead>
            <tr className="bg-[#f3f4f6]">
              {['Select payee', 'Name', 'Reference'].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] border-b border-[#d1d5db]"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={3}
                  className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                >
                  Loading…
                </td>
              </tr>
            ) : payees.length > 0 ? (
              visiblePayees.map((p) => (
                <tr
                  key={p.PaymentId}
                  className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]"
                >
                  <td className="px-4 py-4 border-r border-[#dad7d3]">
                    <input
                      type="checkbox"
                      id={`transfer-payee-${p.PaymentId}`}
                      checked={selectedId === p.PaymentId}
                      onChange={() => {
                        setSelectedId((cur) =>
                          cur === p.PaymentId ? null : (p.PaymentId ?? null),
                        );
                        setShowSelected(false);
                        // A different payee supersedes a prior decline (#151).
                        setSubmitError('');
                        setSubmitForbidden(false);
                        // #485: drop the prior payee's beneficiary BIC/IBAN
                        // (re-fetched on the next "Transfer to Selected"); the
                        // seq bump cancels any in-flight detail fetch.
                        detailSeq.current += 1;
                        setBeneficiaryBic(null);
                        setBeneficiaryIban(null);
                      }}
                      aria-label={`Select ${p.Name ?? 'payee'}`}
                      className="size-[20px] accent-[#FF703E] cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-4 text-[14px] font-['Arial',sans-serif] text-[#1d252c] border-r border-[#dad7d3]">
                    {p.Name}
                  </td>
                  <td className="px-4 py-4 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                    {p.Reference}
                  </td>
                </tr>
              ))
            ) : (
              !loadError && (
                <tr>
                  <td
                    colSpan={3}
                    className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                  >
                    No payees available on this account.
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination + Transfer to selected (CCF-100). #462: the list is paged —
          PAYEES_PER_PAGE rows per screen — so Next/Previous walk the pages
          (disabled at the first/last page respectively). #829: when the gateway
          signals more than the loaded ~100 rows, "Next" lazily fetches the next
          block, so paging walks PAST page 10 (no jump-to-page control —
          @dylan219 2026-09-24). #490: the "Transfer to Selected" button sits
          adjacent to the paging controls. */}
      {payees.length > 0 && (
        <div className="mt-6 flex items-center gap-4">
          <button
            type="button"
            disabled={currentPage <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            aria-label="Previous page"
            className="px-8 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
          >
            &lt; Previous
          </button>
          <button
            type="button"
            disabled={!hasNextPage || loadingMore}
            onClick={() => {
              void goToNextPage();
            }}
            aria-label="Next page"
            className="px-8 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
          >
            {loadingMore ? 'Loading…' : 'Next >'}
          </button>
          <span className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] select-none">
            {/* Total is unknown until the last block is loaded (#829): show
                "Page N of M" when fully loaded, else "Page N" while more remain. */}
            {hasNextPage && hasMore
              ? `Page ${currentPage + 1}`
              : `Page ${currentPage + 1} of ${loadedPageCount}`}
          </span>
          <button
            type="button"
            disabled={!selectedId}
            onClick={viewSelected}
            aria-label="Transfer to selected payee"
            className="px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Transfer to Selected
          </button>
        </div>
      )}

      {/* Selected-payee detail + amount + scripting (CCF-100). */}
      {showSelected && selected && (
        <div className="mt-8 max-w-[700px] border-2 border-[#dad7d3] bg-white p-8">
          <h3 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4">
            Payee details
          </h3>
          <div className="grid grid-cols-[160px_1fr] gap-y-2 mb-8">
            <div className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
              Payment to:
            </div>
            <div className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
              {selected.Name ?? '—'}
            </div>
            <div className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
              Reference:
            </div>
            <div className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
              {selected.Reference ?? '—'}
            </div>
            {/* #485: beneficiary BIC + IBAN from the bill-details op. */}
            <div className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
              BIC:
            </div>
            <div className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
              {beneficiaryBic ?? '—'}
            </div>
            <div className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
              IBAN:
            </div>
            <div className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
              {beneficiaryIban ?? '—'}
            </div>
          </div>

          {done ? (
            <p
              className="text-[16px] font-['Arial',sans-serif] font-bold text-[#16a34a] m-0"
              role="alert"
              aria-live="polite"
            >
              Transfer to payee completed
            </p>
          ) : (
            <>
              {/* Amount + Confirm (CCF-100). */}
              <div className="mb-8 flex items-end gap-4">
                <div className="max-w-[300px]">
                  <PayeeInput
                    id="transfer-payee-amount"
                    label="Amount"
                    value={amount}
                    onChange={(v) => {
                      setAmount(v);
                      // Editing the amount invalidates ALL downstream consent:
                      // the script, timeframe and confirmation were given for a
                      // different figure (money-movement — never carry stale
                      // consent onto a changed amount).
                      setAmountConfirmed(false);
                      setTimeframe('');
                      setScriptingConfirmed(false);
                      // A new amount supersedes a prior decline/guard (#151).
                      setSubmitError('');
                      setSubmitForbidden(false);
                    }}
                    placeholder="€00.00"
                  />
                </div>
                <div className="mb-[20px]">
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        !/^(?!0+(\.0+)?$)\d+(\.\d{1,2})?$/.test(amount.trim())
                      )
                        toast.error('Enter a valid transfer amount.');
                      else setAmountConfirmed(true);
                    }}
                    aria-label="Confirm transfer amount"
                    className="px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
                  >
                    Confirm
                  </button>
                </div>
              </div>

              {amountConfirmed && (
                <>
                  {/* Confirmation scripting (CCF-100). */}
                  <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-6">
                    Thank you, I am now going to transfer €{amount.trim()} from
                    your account ending {accountNumber.slice(-4)} to the payee
                    with the beneficiary name {selected.Name ?? ''} and the
                    reference {selected.Reference ?? ''}.
                  </p>

                  {/* Payment timeframe (CCF-100). Presentation only — drives the
                      agent scripting, not sent to the gateway. */}
                  <div className="mb-6 max-w-[300px]">
                    <Dropdown
                      id="transfer-payee-timeframe"
                      label="Confirm timeframe with customer"
                      placeholder="Select timeframe"
                      value={timeframe}
                      onChange={setTimeframe}
                      options={[
                        { value: 'SEPA', label: 'SEPA' },
                        { value: 'VISA', label: 'VISA' },
                        { value: 'Mortgage', label: 'Mortgage' },
                      ]}
                    />
                  </div>

                  {timeframe && (
                    <div className="mb-6 text-[12px] font-['Arial',sans-serif] text-[#1d252c] leading-relaxed">
                      {timeframe === 'SEPA' && (
                        <p className="m-0">
                          Before 12pm on a working day — funds should reach the
                          third-party account by close of business that day (not
                          guaranteed; may take up to 1 working day). After 12pm
                          or on a non-working day — 1 working day.
                        </p>
                      )}
                      {timeframe === 'VISA' && (
                        <p className="m-0">
                          Before 5:30pm — 2.5 hours (if the VISA card is up to
                          date on payments; otherwise 1 working day). After
                          5:30pm — 1 working day.
                        </p>
                      )}
                      {timeframe === 'Mortgage' && (
                        <p className="m-0">Up to 3 working days.</p>
                      )}
                    </div>
                  )}

                  {/* Customer consent (CCF-100) — only after a timeframe has
                      been confirmed with the customer. */}
                  {timeframe && (
                    <div className="mb-6 flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="transfer-payee-scripting-confirmed"
                        checked={scriptingConfirmed}
                        onChange={() => setScriptingConfirmed((c) => !c)}
                        className="size-[20px] accent-[#FF703E] cursor-pointer"
                      />
                      <label
                        htmlFor="transfer-payee-scripting-confirmed"
                        className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                      >
                        Scripting confirmed
                      </label>
                    </div>
                  )}

                  {timeframe && scriptingConfirmed && (
                    <button
                      type="button"
                      disabled={submitting || blockedBySignatories}
                      title={
                        blockedBySignatories
                          ? MULTIPLE_SIGNATORIES_MESSAGE
                          : undefined
                      }
                      onClick={() => void completeTransfer()}
                      aria-label="Complete transfer"
                      className="px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Working…' : 'Complete Transfer'}
                    </button>
                  )}

                  {/* #799 increment 3 (@dylan219): the FUNDING account requires
                      more than one signatory ("two to sign" mandate), so a
                      third-party payment is NOT permitted through the desktop.
                      The verbatim block message shows inline beside the
                      Complete Transfer button (which is disabled). The agent
                      can select a different account. Enforced server-side on
                      /payments/payee too, so a reload / direct API call cannot
                      bypass it (#173). */}
                  {blockedBySignatories && (
                    <div
                      role="alert"
                      aria-live="polite"
                      className="mt-4 max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                    >
                      {MULTIPLE_SIGNATORIES_MESSAGE}
                    </div>
                  )}

                  {/* Persistent, recoverable inline error beside the Complete
                      Transfer button (#151/#152). The form stays open so the
                      agent can correct the amount/details and retry. The
                      message is the gateway's own Code + Message, shown
                      verbatim (#152). Issue #304: the "re-check the amount/
                      details" recovery hint is a gateway-business-decline
                      affordance and must NOT be shown for an RBAC action
                      denial (`submitForbidden` — #248's verbatim
                      ACTION_DENIED_MESSAGE stands alone). */}
                  {submitError && (
                    <div
                      id="transfer-payee-error"
                      role="alert"
                      aria-live="assertive"
                      className="mt-4 max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                    >
                      <p className="m-0 font-bold">{submitError}</p>
                      {!submitForbidden && (
                        <p className="m-0 mt-1 text-[#6b7280]">
                          Please re-check the amount/details with the customer
                          and try again.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function BillPaymentsScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  const location = useLocation();
  // #637 inc 21 — the resolved content-catalogue map, threaded into the SEPA CT
  // Recall email template so its editable wording (subject/title/advisory
  // bullets/labels/reason options/instruction) reflects any admin reword;
  // absent-key defaults keep the output unchanged.
  const contentMap = useContentMap();

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  // Top-level payment option. Starts UNSELECTED (empty) so the agent must
  // actively choose an option before any flow proceeds — no payment type is
  // pre-selected (issue #364, authoritative @dylan219). The Bill Payments flow
  // (this screen's route `/payments`) and its sub-options/account selectors
  // render only once the agent picks "Bill Payments"; the other three options
  // route away to their own screens (see onPaymentTypeChange). This supersedes
  // the earlier default-to-"Bill Payments" behaviour (#116).
  //
  // EXCEPTION (issue #501, Option A, authoritative @dylan219): when the agent
  // reaches `/payments` by picking "Bill Payments" from another payment
  // screen's shared Payment-options selector (PaymentOptionsNav), that selection
  // IS the agent actively choosing — the nav passes `{ paymentOption: 'Bill
  // Payments' }` as navigation state, so we open with it already selected
  // instead of forcing a second selection. A fresh arrival (dashboard / process
  // menu) carries no such state, so no option is pre-selected (#364 intact).
  const [paymentType, setPaymentType] = useState(() =>
    (location.state as { paymentOption?: string } | null)?.paymentOption ===
    'Bill Payments'
      ? 'Bill Payments'
      : '',
  );
  const [billOption, setBillOption] = useState(''); // Bill Payments sub-option
  const [selectedAccount, setSelectedAccount] = useState('');
  const [createPayeeAccount, setCreatePayeeAccount] = useState('');
  const [transferPayeeAccount, setTransferPayeeAccount] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const [payments, setPayments] = useState<BillPaymentRow[]>([]);
  // Client-side pagination of the loaded overview list (#481). Named distinctly
  // from the payee selector's `page`/`setPage` (in TransferToPayeeSection) to
  // avoid any collision. Reset to 0 whenever the list is (re)loaded.
  const [overviewPage, setOverviewPage] = useState(0);
  // #829 (server-side pagination): the gateway returns at most ~100 payments per
  // call. `overviewHasMore` mirrors the response `hasMore` for the currently
  // loaded block and the cursor is the continuation to fetch the NEXT ~100. When
  // the agent steps to a UI page whose rows aren't loaded yet AND
  // `overviewHasMore`, we lazily re-call bill-list with the cursor and APPEND the
  // next block, so Previous/Next walks PAST page 10 for >100 records (no
  // jump-to-page — @dylan219 2026-09-24). Per @dylan219 answer #2 this applies
  // ONLY to the Active stream (`includeCancelled === false`); the combined
  // Active+Others path (`includeCancelled === true`) has no single cursor, so it
  // keeps the pure client-side windowing over the loaded block.
  const [overviewHasMore, setOverviewHasMore] = useState(false);
  const [overviewLoadingMore, setOverviewLoadingMore] = useState(false);
  const overviewCursorRef = useRef<{ seq?: string; no?: string }>({});
  const [showResults, setShowResults] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null,
  );
  const [detail, setDetail] = useState<BillPaymentDetail | null>(null);
  const [confirmDetails, setConfirmDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');

  // Monotonic request sequences: a slow earlier response must not overwrite the
  // state of a newer load (e.g. the agent switches account / toggles the filter
  // / opens a different payment before the previous request resolves).
  const listSeq = useRef(0);
  const detailSeq = useRef(0);

  // Editable beneficiary fields (CCF-67) — controlled so an amend can submit.
  const [editName, setEditName] = useState('');
  const [editRef, setEditRef] = useState('');
  const [editRemit, setEditRemit] = useState('');
  const [nameError, setNameError] = useState('');
  const [refError, setRefError] = useState('');
  const [remitError, setRemitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Inline, non-blocking, retryable outcome of an Amend/Cancel action — the
  // gateway's Code + Message shown beside the buttons, plus a success
  // confirmation (authoritative @dylan219, issue #87). Mirrored to a toast for
  // immediacy.
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  // #269: the trailing trashcan on each listing row starts a cancel with a
  // confirmation step (mirrors the Direct Debits / Standing Orders row-click +
  // trashcan UX, #255). Holds the row awaiting a Confirm/Keep decision; cleared
  // on Keep, on a successful cancel, or when a different row's trashcan is used.
  const [cancelRow, setCancelRow] = useState<BillPaymentRow | null>(null);
  // The trashcan cancel is a state-changing banking action, so it may only fire
  // after the agent EXPLICITLY confirms the details with the customer (CCF-68 —
  // parity with the detail-panel "Confirm all details" gate). This tick gates
  // the dialog's Confirm-cancellation button; reset whenever the dialog opens or
  // is dismissed so consent is never carried over between rows.
  const [cancelRowConfirmed, setCancelRowConfirmed] = useState(false);
  // Inline, recoverable gateway decline for the trashcan cancel (Code + Message
  // beside the confirmation, mirrored to a toast) — the row-level twin of
  // `actionError` used by the detail-panel Cancel (authoritative @dylan219 #87).
  const [cancelRowError, setCancelRowError] = useState('');

  // Load the customer's accounts for the account-selector dropdown.
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasContext) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      // Context is now valid — clear any stale "no verified customer" message
      // left over from an earlier render where context had not yet populated.
      setError('');
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/accounts',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        if (apiError || !data) {
          setError('Could not load accounts.');
          return;
        }
        setAccounts((data.Accounts ?? []) as unknown as AccountRow[]);
      } catch {
        if (!cancelled) setError('Request failed.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  // #799 (@dylan219, increment 3) — the payee WRITE actions (amend a payee,
  // create payee / third-party payee, transfer to payee) are NOT permitted on an
  // account that requires MORE THAN ONE signatory ("two to sign" mandate); the
  // change has to go through the mandate. The signal is the BFF-normalised
  // per-account `Signatories` count (from the gateway `NumofSignatories`), read
  // via the SHARED helper so all blocked action-areas apply the identical rule.
  // Each action's own account-selector drives its flag: the overview amend reads
  // the OVERVIEW account, Create payee the create-payee account, Transfer to
  // payee the transfer account. Blocks only on a KNOWN count > 1 — an absent
  // count never blocks. The controls are suppressed client-side AND the
  // verbatim message shown inline; the matching BFF routes enforce the SAME
  // block server-side (#173), so a reload / direct call cannot bypass it. Bill
  // payment CANCELLATION is a separate action (not one of the six blocked
  // areas) and is deliberately unaffected.
  const accountBlocksAmend = requiresMultipleSignatories(
    accounts.find((a) => a.AccountNumber === selectedAccount),
  );
  const accountBlocksCreatePayee = requiresMultipleSignatories(
    accounts.find((a) => a.AccountNumber === createPayeeAccount),
  );
  const accountBlocksTransferPayee = requiresMultipleSignatories(
    accounts.find((a) => a.AccountNumber === transferPayeeAccount),
  );

  function resetDetail(): void {
    setSelectedPaymentId(null);
    setDetail(null);
    setConfirmDetails(false);
    setEditName('');
    setEditRef('');
    setEditRemit('');
    setNameError('');
    setRefError('');
    setRemitError('');
    setActionError('');
    // Any pending trashcan confirmation is dismissed alongside the detail panel
    // (#269) — a row-click / new selection supersedes an unconfirmed cancel.
    setCancelRow(null);
    setCancelRowConfirmed(false);
    setCancelRowError('');
    // NB: actionSuccess is intentionally NOT cleared here — a successful
    // Amend/Cancel closes the detail panel via resetDetail() but the success
    // confirmation must persist above the refreshed listing (#87). It is
    // cleared when the agent opens another payment or starts a new action.
  }

  function resetResults(): void {
    setShowResults(false);
    setPayments([]);
    setOverviewPage(0);
    // #829: clear the server-pagination cursor state alongside the loaded rows.
    setOverviewHasMore(false);
    overviewCursorRef.current = {};
    resetDetail();
  }

  async function loadPayments(
    accountNumber: string,
    cancelledFlag: boolean,
  ): Promise<void> {
    if (!accountNumber) return;
    const seq = ++listSeq.current;
    setLoading(true);
    setError('');
    setSelectedPaymentId(null);
    setDetail(null);
    setConfirmDetails(false);
    // #829: a fresh load starts a new pagination window — reset the cursor state
    // (mirrors `setOverviewPage(0)` below). It is repopulated from the response
    // only for the Active stream (`!cancelledFlag`); the combined path carries no
    // single cursor.
    setOverviewHasMore(false);
    overviewCursorRef.current = {};
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/bill-list', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber,
          ...(cancelledFlag ? { includeCancelled: true } : {}),
        },
      });
      // A newer load superseded this one — drop the stale response.
      if (seq !== listSeq.current) return;
      if (apiError || !data) {
        setError(
          response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : `Could not load bill payments (${response.status}).`,
        );
        return;
      }
      const rows = (data.Payments ?? []) as unknown as BillPaymentRow[];
      setPayments(rows);
      setOverviewPage(0);
      // #829: server-side cursor pagination applies ONLY to the Active stream
      // (@dylan219 answer #2). For the combined Active+Others path
      // (`cancelledFlag`) the BFF returns no single cursor, so leave `hasMore`
      // false and keep the pure client-side windowing over the loaded block.
      if (!cancelledFlag) {
        setOverviewHasMore(data.hasMore ?? false);
        overviewCursorRef.current = {
          seq: data.nextPaymentsSeq,
          no: data.nextPaymentsNo,
        };
      }
      setShowResults(true);
      if (!rows.length) toast.info('No bill payments available.');
    } catch {
      if (seq === listSeq.current) setError('Request failed.');
    } finally {
      if (seq === listSeq.current) setLoading(false);
    }
  }

  /**
   * #829: fetch the NEXT ~100-payment block from the gateway using the
   * continuation cursor and APPEND it to the loaded rows. Called when the agent
   * steps to an overview UI page whose rows aren't loaded yet and
   * `overviewHasMore` is true (Active stream only). Guards against a concurrent
   * fetch (`overviewLoadingMore`) and a missing cursor. On failure the loaded
   * rows are left intact and `overviewHasMore` is cleared so "Next" settles on
   * the last loaded page rather than retrying forever; the agent can retry by
   * re-selecting the account. Does NOT send `includeCancelled` — the cursor path
   * is the Active stream by construction (@dylan219 answer #2).
   */
  async function loadMorePayments(): Promise<boolean> {
    if (overviewLoadingMore || !overviewHasMore) return false;
    const cursor = overviewCursorRef.current;
    if (!cursor.seq && !cursor.no) return false;
    setOverviewLoadingMore(true);
    try {
      const { data, error: apiError } = await api.POST('/payments/bill-list', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: selectedAccount,
          ...(cursor.seq ? { nextPaymentsSeq: cursor.seq } : {}),
          ...(cursor.no ? { nextPaymentsNo: cursor.no } : {}),
        },
      });
      if (apiError || !data) {
        setOverviewHasMore(false);
        return false;
      }
      const more = (data.Payments ?? []) as unknown as BillPaymentRow[];
      setPayments((cur) => [...cur, ...more]);
      setOverviewHasMore(data.hasMore ?? false);
      overviewCursorRef.current = {
        seq: data.nextPaymentsSeq,
        no: data.nextPaymentsNo,
      };
      return more.length > 0;
    } catch {
      setOverviewHasMore(false);
      return false;
    } finally {
      setOverviewLoadingMore(false);
    }
  }

  /**
   * #829: step to the next overview UI page. If the next page's rows aren't
   * loaded yet and the gateway signals more (Active stream), fetch the next
   * ~100-row block first, then advance. "Next" is disabled only on the last
   * loaded page when `overviewHasMore` is false (handled in the render).
   */
  async function goToNextOverviewPage(): Promise<void> {
    const nextStart = (overviewPage + 1) * BILL_PAYMENTS_PER_PAGE;
    if (nextStart >= payments.length && overviewHasMore) {
      await loadMorePayments();
    }
    setOverviewPage((p) => p + 1);
  }

  async function openPayment(payee: BillPaymentRow): Promise<void> {
    const paymentId = payee.PaymentId ?? '';
    const seq = ++detailSeq.current;
    setSelectedPaymentId(paymentId);
    setDetail(null);
    setConfirmDetails(false);
    setActionError('');
    setActionSuccess('');
    setDetailLoading(true);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/bill', {
        params: { header: { 'X-Teller-Id': tellerId } },
        // The real gateway keys the detail lookup on PaymentsSeq + PaymentsNo
        // (from the listing row), not the opaque paymentId — send both.
        body: {
          customerNumber,
          accountNumber: selectedAccount,
          paymentId,
          ...(payee.PaymentsSeq ? { paymentsSeq: payee.PaymentsSeq } : {}),
          ...(payee.PaymentsNo ? { paymentsNo: payee.PaymentsNo } : {}),
        },
      });
      // A newer open/load superseded this one — drop the stale response so it
      // can't render a different payment's details under the current selection.
      if (seq !== detailSeq.current) return;
      if (apiError || !data || (data as { Found?: boolean }).Found === false) {
        toast.error(
          response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : 'Could not load bill payment details. Please retry.',
        );
        return;
      }
      // The gateway `bill-details` op returns the beneficiary block + status/
      // type but NOT the amend/cancel keys (`PaymentsSeq`/`PaymentsNo`) — those
      // are carried on the listing row. Merge the selected row's keys (+ a
      // PaymentId/Status/Name/Reference fallback) beneath the detail response
      // (detail wins when it carries a value) so the form populates AND
      // Amend/Cancel can identify the payment on the real gateway (#87). Without
      // this the panel populated but Amend/Cancel was blocked "missing
      // reference". Mirrors the standing-orders row-merge (#86).
      const fetched = data as unknown as BillPaymentDetail;
      const d: BillPaymentDetail = {
        PaymentId: payee.PaymentId,
        PaymentsSeq: payee.PaymentsSeq,
        PaymentsNo: payee.PaymentsNo,
        Status: payee.Status,
        Name: payee.Name,
        Reference: payee.Reference,
        ...Object.fromEntries(
          Object.entries(fetched).filter(
            ([, v]) => v !== undefined && v !== null && v !== '',
          ),
        ),
      };
      setDetail(d);
      setEditName(d.BeneficiaryName ?? '');
      setEditRef(d.BeneficiaryReference ?? '');
      setEditRemit(d.RemitData ?? '');
      setNameError('');
      setRefError('');
      setRemitError('');
    } catch {
      if (seq === detailSeq.current)
        toast.error('Could not load bill payment details. Please retry.');
    } finally {
      if (seq === detailSeq.current) setDetailLoading(false);
    }
  }

  /** Amend the open payment's beneficiary fields (CCF-67). */
  async function amendPayment(): Promise<void> {
    // #799 increment 3 — defence in depth: the Amend button is already hidden
    // on a >1-signatory account, but re-assert the block here so the amend op
    // can never fire for a "two to sign" account. The server enforces the same
    // block (#173).
    if (accountBlocksAmend) {
      setActionError(MULTIPLE_SIGNATORIES_MESSAGE);
      toast.error(MULTIPLE_SIGNATORIES_MESSAGE);
      return;
    }
    if (!detail?.PaymentsSeq || !detail.PaymentsNo) {
      toast.error('This payment cannot be amended (missing reference).');
      return;
    }
    const nErr = validateField('Beneficiary name', editName, 18, true);
    const rErr = validateField('Beneficiary reference', editRef, 18, true);
    const mErr = validateField('Remit data', editRemit, 40, false, true);
    setNameError(nErr);
    setRefError(rErr);
    setRemitError(mErr);
    if (nErr || rErr || mErr) return;

    setSubmitting(true);
    setActionError('');
    setActionSuccess('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/bill-amend', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: selectedAccount,
          paymentsSeq: detail.PaymentsSeq,
          paymentsNo: detail.PaymentsNo,
          beneficiaryName: editName,
          beneficiaryReference: editRef,
          ...(editRemit ? { remitData: editRemit } : {}),
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Inline, non-blocking, retryable: gateway Code + Message beside the
        // button (the form stays open so the agent can retry — #87). Issue #248:
        // an RBAC action denial (amendBillPayment) refuses with 403
        // `{ error: 'forbidden' }` — surface @dylan219's verbatim copy inline,
        // never a raw 403. Distinct from the gateway business-decline
        // (`formatActionError`) and the Teller-ID gate (`invalid_teller_id`, a
        // different 403 body `resolveActionBlockMessage` deliberately ignores).
        // #799 increment 3: the server-side multi-signatory block
        // (`ResultCode: 'MULTIPLE_SIGNATORIES'`, the gateway never called) shows
        // the verbatim advisory — no code prefix (the sentinel is an APP code,
        // not a gateway ErrorNumber).
        const message =
          resolveActionBlockMessage(response.status, apiError) ??
          signatoryBlockMessage(
            (data ?? {}) as { ResultCode?: string; ResultMessage?: string },
          ) ??
          (response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : formatActionError(
                data as
                  | { ResultCode?: string; ResultMessage?: string }
                  | undefined,
                'Could not amend the bill payment. Please retry.',
                BILL_AMEND_CODE_REASON,
              ));
        setActionError(message);
        toast.error(message);
        return;
      }
      setActionSuccess('Bill payment amended successfully.');
      toast.success('Bill payment amended successfully.');
      resetDetail();
      await loadPayments(selectedAccount, includeCancelled);
    } catch {
      const message = 'Could not amend the bill payment. Please retry.';
      setActionError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Post the bill-cancel (CCF-68) for a payment identified by its listing keys
   * (PaymentsSeq/PaymentsNo). Shared by the detail-panel "Cancel bill payment"
   * button and the per-row trashcan (#269): both route through the SAME gateway
   * op + success/decline handling. Returns true on a real gateway success. The
   * inline decline is surfaced via `onError` (the detail panel and the row
   * confirmation each render their own recoverable alert — @dylan219 #87).
   */
  async function submitBillCancel(
    keys: { paymentsSeq?: string; paymentsNo?: string },
    onError: (message: string) => void,
  ): Promise<boolean> {
    if (!keys.paymentsSeq || !keys.paymentsNo) {
      const message = 'This payment cannot be cancelled (missing reference).';
      onError(message);
      toast.error(message);
      return false;
    }
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/bill-cancel', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: selectedAccount,
          paymentsSeq: keys.paymentsSeq,
          paymentsNo: keys.paymentsNo,
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Issue #248: an RBAC action denial (cancelBillPayment) refuses with 403
        // `{ error: 'forbidden' }` — surface @dylan219's verbatim copy inline,
        // never a raw 403. Distinct from the gateway business-decline
        // (`formatActionError`) and the Teller-ID gate (`invalid_teller_id`, a
        // different 403 body `resolveActionBlockMessage` deliberately ignores).
        const message =
          resolveActionBlockMessage(response.status, apiError) ??
          (response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : formatActionError(
                data as
                  | { ResultCode?: string; ResultMessage?: string }
                  | undefined,
                'Could not cancel the bill payment. Please retry.',
              ));
        onError(message);
        toast.error(message);
        return false;
      }
      return true;
    } catch {
      const message = 'Could not cancel the bill payment. Please retry.';
      onError(message);
      toast.error(message);
      return false;
    }
  }

  /** Cancel the open payment from the detail panel (CCF-68). */
  async function cancelPayment(): Promise<void> {
    if (!detail?.PaymentsSeq || !detail.PaymentsNo) {
      toast.error('This payment cannot be cancelled (missing reference).');
      return;
    }
    setSubmitting(true);
    setActionError('');
    setActionSuccess('');
    const ok = await submitBillCancel(
      { paymentsSeq: detail.PaymentsSeq, paymentsNo: detail.PaymentsNo },
      setActionError,
    );
    if (ok) {
      setActionSuccess('Bill payment cancelled successfully.');
      toast.success('Bill payment cancelled successfully.');
      resetDetail();
      await loadPayments(selectedAccount, includeCancelled);
    }
    setSubmitting(false);
  }

  /**
   * Cancel a bill payment straight from its listing-row trashcan (#269), after
   * the inline Confirm step. Uses the row's own PaymentsSeq/PaymentsNo (the
   * listing carries them — the gateway detail op does not, #87), so no extra
   * detail fetch is needed. On success it clears the confirmation, surfaces the
   * shared success confirmation + toast, and refreshes the listing. RBAC + the
   * Teller-ID gate are enforced server-side on /payments/bill-cancel exactly as
   * for the detail-panel Cancel — the trashcan changes only the UI entry point.
   */
  async function confirmCancelRow(row: BillPaymentRow): Promise<void> {
    setSubmitting(true);
    setCancelRowError('');
    setActionSuccess('');
    const ok = await submitBillCancel(
      { paymentsSeq: row.PaymentsSeq, paymentsNo: row.PaymentsNo },
      setCancelRowError,
    );
    if (ok) {
      setCancelRow(null);
      setCancelRowConfirmed(false);
      setActionSuccess('Bill payment cancelled successfully.');
      toast.success('Bill payment cancelled successfully.');
      resetDetail();
      await loadPayments(selectedAccount, includeCancelled);
    }
    setSubmitting(false);
  }

  function onPaymentTypeChange(value: string): void {
    // Standing orders are a separate screen (SCRN-35..37); route there rather
    // than showing a "not yet available" note for the one built sub-flow.
    if (value === 'Standing orders') {
      navigate('/standing-orders');
      return;
    }
    // Funds Transfer (non-notice internal transfer) is a separate screen
    // (SCRN-32 / CCF-69); route there.
    if (value === 'Transfer between accounts') {
      navigate('/funds-transfer');
      return;
    }
    // Direct Debits are a separate screen (SCRN-33/34 / CCF-71 / CCF-102);
    // route there rather than the "not yet available" note (issue #64).
    if (value === 'Direct Debits') {
      navigate('/direct-debits');
      return;
    }
    setPaymentType(value);
    setBillOption('');
    setSelectedAccount('');
    setCreatePayeeAccount('');
    setTransferPayeeAccount('');
    setIncludeCancelled(false);
    resetResults();
  }

  function onBillOptionChange(value: string): void {
    setBillOption(value);
    setSelectedAccount('');
    setCreatePayeeAccount('');
    setTransferPayeeAccount('');
    setIncludeCancelled(false);
    resetResults();
  }

  function onAccountChange(value: string): void {
    setSelectedAccount(value);
    setIncludeCancelled(false);
    resetResults();
    void loadPayments(value, false);
  }

  function onToggleCancelled(): void {
    const next = !includeCancelled;
    setIncludeCancelled(next);
    if (selectedAccount) void loadPayments(selectedAccount, next);
  }

  // Client-side pagination of the overview list (#481 — "List lengths"): render
  // only the current page's slice of BILL_PAYMENTS_PER_PAGE (10) rows; the agent
  // steps through pages with Previous/Next. At least one page always exists; the
  // index is clamped so a shrunk list (e.g. after a cancel) can't leave the view
  // on an out-of-range page. Mirrors Standing Orders (#437) / the payee list.
  const overviewLoadedPageCount = Math.max(
    1,
    Math.ceil(payments.length / BILL_PAYMENTS_PER_PAGE),
  );
  // #829: more UI pages may exist beyond the loaded rows when the gateway signals
  // `overviewHasMore` (Active stream), so allow the index to reach one page past
  // the loaded rows; clamp defensively.
  const overviewCurrentPage = Math.min(
    overviewPage,
    overviewLoadedPageCount - 1 + (overviewHasMore ? 1 : 0),
  );
  const visiblePayments = payments.slice(
    overviewCurrentPage * BILL_PAYMENTS_PER_PAGE,
    overviewCurrentPage * BILL_PAYMENTS_PER_PAGE + BILL_PAYMENTS_PER_PAGE,
  );
  // "Next" is enabled while there is another LOADED page OR the gateway signals
  // more rows to fetch (#829). Disabled only on the last loaded page with no more.
  const overviewHasNextPage =
    overviewCurrentPage < overviewLoadedPageCount - 1 || overviewHasMore;

  // #530: utility / VISA / mortgage-type billers have a FIXED beneficiary name
  // (the gateway refuses a name change with 9287) — grey the name field out in
  // the amend form so the agent can't attempt it. Reference/remit stay editable.
  const detailNameLocked = isBillNameLocked(detail?.Type);

  // #579 (follow-up): for a non-active (cancelled/lapsed/completed) payment the
  // Amend button is hidden and the Beneficiary reference + Remit data fields are
  // rendered greyed-out / read-only — the agent can VIEW the values but cannot
  // edit them (there is nothing to amend on an already-cancelled payment).
  // Mirrors the Cancel-button gate already in place from !856.
  const detailIsActive = isBillPaymentActive(detail?.Status);

  // #658 (CCRS-3427): the beneficiary name is editable only for an ACTIVE payment
  // whose biller type also permits a name change (#530). For a cancelled/lapsed/
  // completed payment the name is greyed-out / read-only like the reference +
  // remit fields (#579), and the "Confirm all details" tick is hidden entirely.
  const nameEditable = detailIsActive && !detailNameLocked;

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="payments-heading"
    >
      <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>

        {/* Email templates (#442, mirroring #441): the payments-surface email
            templates (the SEPA CT Recall pack shared with Standing Orders) are
            consolidated into a single top-right dropdown selector in the page
            header, from which the agent picks one. Each pops the source
            template prefilled best-effort from the in-context customer name and
            the debited account number, delivered as a formatted downloadable
            Outlook draft (`.eml`, #434) where the template provides `buildHtml`,
            else a plain-text `mailto:` draft. Nothing is sent from the app: the
            agent completes + sends the draft in Outlook. (Replaces the previous
            buried button row inside the View/Amend sub-flow — per issue #442.) */}
        <EmailTemplateMenu
          templates={STANDING_ORDERS_TEMPLATES}
          context={{
            customerName: customer.name ?? '',
            accountNumber: selectedAccount,
            content: contentMap,
          }}
        />
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="payments-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-8"
        >
          Payments
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {/* Payment option + bill-payments sub-option + account selection. */}
      <div className="mb-6 flex gap-6 flex-wrap items-start">
        <div className="w-[400px]">
          <Dropdown
            id="payment-type"
            label="Payment options"
            placeholder="Select payment option"
            value={paymentType}
            onChange={onPaymentTypeChange}
            options={[
              {
                value: 'Transfer between accounts',
                label: 'Transfer between accounts',
              },
              { value: 'Bill Payments', label: 'Bill Payments' },
              { value: 'Standing orders', label: 'Standing orders' },
              { value: 'Direct Debits', label: 'Direct Debits' },
            ]}
          />
        </div>

        {paymentType === 'Bill Payments' && (
          <div className="w-[400px]">
            <Dropdown
              id="bill-payment-option"
              label="Bill Payments"
              placeholder="Select"
              value={billOption}
              onChange={onBillOptionChange}
              options={[
                {
                  value: 'Bill payment overview',
                  label: 'Bill payment overview',
                },
                { value: 'Create payee', label: 'Create payee' },
                { value: 'Transfer to payee', label: 'Transfer to payee' },
              ]}
            />
          </div>
        )}

        {paymentType === 'Bill Payments' &&
          billOption === 'Bill payment overview' && (
            <div className="w-[400px]">
              <Dropdown
                id="bill-overview-account-selection"
                label="Select account to view bill payments"
                placeholder="Select"
                value={selectedAccount}
                onChange={onAccountChange}
                options={accounts.map((a) => ({
                  value: a.AccountNumber,
                  // Canonical account-dropdown label (#381) — matches Standing
                  // Orders / Transfer: "<name> <last4>", masked per CCF-80
                  // (#130/#131 won't-fix). Never the full account number.
                  label: accountOptionLabel(a),
                }))}
              />
            </div>
          )}

        {paymentType === 'Bill Payments' && billOption === 'Create payee' && (
          <div className="w-[400px]">
            <Dropdown
              id="create-payee-account-selection"
              label="Select account"
              placeholder="Select"
              value={createPayeeAccount}
              onChange={setCreatePayeeAccount}
              options={accounts.map((a) => ({
                value: a.AccountNumber,
                // Canonical account-dropdown label (#381) — matches Standing
                // Orders / Transfer: "<name> <last4>", masked per CCF-80
                // (#130/#131 won't-fix). Never the full account number.
                label: accountOptionLabel(a),
              }))}
            />
          </div>
        )}

        {paymentType === 'Bill Payments' &&
          billOption === 'Transfer to payee' && (
            <div className="w-[400px]">
              <Dropdown
                id="transfer-payee-account-selection"
                label="Select account to transfer to payee"
                placeholder="Select"
                value={transferPayeeAccount}
                onChange={setTransferPayeeAccount}
                options={accounts.map((a) => ({
                  value: a.AccountNumber,
                  // Canonical account-dropdown label (#381) — matches Standing
                  // Orders / Transfer: "<name> <last4>", masked per CCF-80
                  // (#130/#131 won't-fix). Never the full account number.
                  label: accountOptionLabel(a),
                }))}
              />
            </div>
          )}
      </div>

      {/* Payment types / options not yet built (no backend) — honest note
          rather than a broken flow. */}
      {paymentType && paymentType !== 'Bill Payments' && (
        <p className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] mb-6">
          {paymentType} is not yet available in this release.
        </p>
      )}
      {paymentType === 'Bill Payments' &&
        billOption &&
        billOption !== 'Bill payment overview' &&
        billOption !== 'Create payee' &&
        billOption !== 'Transfer to payee' && (
          <p className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] mb-6">
            {billOption} is not yet available in this release.
          </p>
        )}

      {/* Create payee — Add payee / Add VISA / Add mortgage (CCF-65). */}
      {paymentType === 'Bill Payments' &&
        billOption === 'Create payee' &&
        createPayeeAccount && (
          <CreatePayeeSection
            key={createPayeeAccount}
            customerNumber={customerNumber}
            tellerId={tellerId}
            accountNumber={createPayeeAccount}
            blockedBySignatories={accountBlocksCreatePayee}
          />
        )}

      {/* Transfer to payee — select payee, amount, scripting (SCRN 30 /
          CCF-100). */}
      {paymentType === 'Bill Payments' &&
        billOption === 'Transfer to payee' &&
        transferPayeeAccount && (
          <TransferToPayeeSection
            key={transferPayeeAccount}
            customerNumber={customerNumber}
            tellerId={tellerId}
            accountNumber={transferPayeeAccount}
            blockedBySignatories={accountBlocksTransferPayee}
          />
        )}

      {/* Bill payment overview — listing + detail (CCF-64 / CCF-66). */}
      {paymentType === 'Bill Payments' &&
        billOption === 'Bill payment overview' &&
        selectedAccount && (
          <div className="mt-2">
            {/* Success confirmation for a completed Amend/Cancel — persists
                above the refreshed listing after the detail panel closes (#87). */}
            {actionSuccess && (
              <div
                role="alert"
                aria-live="polite"
                className="mb-6 max-w-[1100px] p-3 bg-white border border-[#16a34a] font-['Arial',sans-serif] text-[14px] font-bold text-[#16a34a]"
              >
                {actionSuccess}
              </div>
            )}
            {/* Show-cancelled toggle (CCF-64). */}
            <div className="mb-6">
              <Toggle
                id="show-cancelled-bills-toggle"
                label="Show cancelled bill payments"
                checked={includeCancelled}
                onChange={onToggleCancelled}
              />
            </div>

            {/* Bill payments table — Name / Reference / Status / Type (CCF-64).
                Clicking a row opens its detail directly — no per-row
                "View / Amend" button (#269). A trailing trashcan column starts
                the cancel-bill-payment flow (see below). */}
            <div className="overflow-x-auto max-w-[1100px]">
              <table className="w-full border-collapse bg-white" role="table">
                <thead>
                  <tr className="bg-[#f3f4f6]">
                    {['Name', 'Reference', 'Status', 'Type'].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] border-b border-[#d1d5db]"
                      >
                        {h}
                      </th>
                    ))}
                    {/* Trailing action (trashcan) column — no visible header. */}
                    <th
                      className="px-4 py-3 border-b border-[#d1d5db]"
                      aria-label="Actions"
                    />
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                      >
                        Loading…
                      </td>
                    </tr>
                  ) : payments.length > 0 ? (
                    visiblePayments.map((p) => {
                      const isSelected = selectedPaymentId === p.PaymentId;
                      const label = p.Name ?? 'bill payment';
                      // A full-cell clickable button per data cell so the whole
                      // row opens the detail on click and stays
                      // keyboard-accessible (#269, mirroring Standing Orders
                      // #267 / Direct Debits #255). Only the primary Name cell
                      // carries the "View …" accessible label and is
                      // keyboard-tabbable, so the row has exactly one focusable
                      // named open control (strict-mode friendly). The remaining
                      // cells are NOT keyboard-tabbable (tabIndex=-1) but stay in
                      // the accessibility tree — each keeps its own visible text
                      // as its accessible name (so screen-reader users can still
                      // read Reference / Status / Type per row).
                      const cellBtn = (
                        value: React.ReactNode,
                        labelled: boolean,
                      ): React.JSX.Element => (
                        <button
                          type="button"
                          onClick={() => p.PaymentId && void openPayment(p)}
                          {...(labelled
                            ? { 'aria-label': `View ${label}` }
                            : { tabIndex: -1 })}
                          className="w-full h-full px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] text-[#1d252c] bg-transparent border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#005fcc]"
                        >
                          {value}
                        </button>
                      );
                      return (
                        <tr
                          key={p.PaymentId}
                          aria-selected={isSelected}
                          className={`border-b border-[#e5e7eb] ${
                            isSelected ? 'bg-[#f3f4f6]' : 'hover:bg-[#f9fafb]'
                          }`}
                        >
                          <td className="p-0">{cellBtn(p.Name, true)}</td>
                          <td className="p-0">{cellBtn(p.Reference, false)}</td>
                          <td className="p-0">
                            {cellBtn(
                              p.Status
                                ? (STATUS_LABEL[p.Status] ?? p.Status)
                                : '',
                              false,
                            )}
                          </td>
                          <td className="p-0">{cellBtn(p.Type, false)}</td>
                          {/* Delete (trashcan) — starts the cancel-bill-payment
                              flow (#269, CCF-68). Opens a confirmation DIALOG
                              (below) rather than cancelling on the first click,
                              so the agent must explicitly confirm the
                              cancellation with the customer first; the actual
                              cancel routes through the same RBAC + Teller-ID +
                              verification-gated /payments/bill-cancel op as the
                              detail-panel Cancel. Mirrors Standing Orders #267.
                              #579: shown ONLY for ACTIVE payments — an already
                              cancelled/lapsed/completed payment cannot be
                              cancelled, so the control is omitted (the cell stays
                              for column alignment). */}
                          <td className="px-4 py-3 text-right w-px">
                            {isBillPaymentActive(p.Status) && (
                              <button
                                type="button"
                                aria-label={`Delete ${label}`}
                                onClick={() => {
                                  setCancelRow(p);
                                  setCancelRowConfirmed(false);
                                  setCancelRowError('');
                                  setActionSuccess('');
                                }}
                                className="p-2 rounded text-[#1D252C] hover:bg-[#f3f4f6] focus:outline-none focus:ring-2 focus:ring-[#005fcc] transition-all"
                              >
                                <svg
                                  className="size-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    showResults && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                        >
                          No bill payments available.
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination buttons (#481 — "List lengths"). Client-side over the
                loaded overview list — at most BILL_PAYMENTS_PER_PAGE (10) rows
                per page; the agent steps through with Previous/Next. Distinct
                aria-labels ("… bill payments page") keep these from colliding
                with the payee-selector Previous/Next on the same screen. */}
            {showResults && payments.length > 0 && (
              <div className="mt-6 flex items-center gap-4">
                <button
                  type="button"
                  disabled={overviewCurrentPage <= 0}
                  onClick={() => setOverviewPage((p) => Math.max(0, p - 1))}
                  aria-label="Previous bill payments page"
                  className="px-8 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
                >
                  &lt; Previous
                </button>
                <span
                  aria-live="polite"
                  className="text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                >
                  {/* Total is unknown until the last block is loaded (#829): show
                      "Page N" while more Active rows remain, else "Page N of M". */}
                  {overviewHasNextPage && overviewHasMore
                    ? `Page ${overviewCurrentPage + 1}`
                    : `Page ${overviewCurrentPage + 1} of ${overviewLoadedPageCount}`}
                </span>
                <button
                  type="button"
                  disabled={!overviewHasNextPage || overviewLoadingMore}
                  onClick={() => {
                    void goToNextOverviewPage();
                  }}
                  aria-label="Next bill payments page"
                  className="px-8 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
                >
                  {overviewLoadingMore ? 'Loading…' : 'Next >'}
                </button>
              </div>
            )}

            {/* Trashcan cancel confirmation DIALOG (#269 / CCF-68). Clicking a
                row's trashcan does NOT cancel immediately — it opens this modal
                so the agent must review the payment with the customer AND tick
                the explicit "Confirm all details" consent (parity with the
                detail-panel Cancel's mandated confirmation checkbox — a
                state-changing banking action can never fire on a single click).
                Confirm posts /payments/bill-cancel (RBAC + Teller-ID +
                verification gated, the same op as the detail-panel Cancel); Keep
                dismisses it. A gateway decline surfaces inline (Code + Message)
                and the dialog stays open so the agent can retry (#87). Mirrors
                the Standing Orders row-trashcan dialog (#267). */}
            {cancelRow && (
              <div
                className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cancel-bill-dialog-title"
              >
                <div className="w-full max-w-[520px] rounded-lg border-2 border-[#dad7d3] bg-white p-8 shadow-xl">
                  <h2
                    id="cancel-bill-dialog-title"
                    className="text-[20px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4"
                  >
                    Cancel bill payment?
                  </h2>
                  <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-6 m-0">
                    This will cancel the bill payment
                    {cancelRow.Name ? ` to ${cancelRow.Name}` : ''}
                    {cancelRow.Reference ? ` (${cancelRow.Reference})` : ''}.
                    This cannot be undone. Confirm the cancellation with the
                    customer before proceeding.
                  </p>
                  {/* Explicit consent — the live cancel op is gated behind this
                      tick (CCF-68), so the trashcan can never bypass the
                      mandated confirmation. */}
                  <div className="mb-6 flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="cancel-bill-confirm-details"
                      checked={cancelRowConfirmed}
                      onChange={() => setCancelRowConfirmed((c) => !c)}
                      className="size-[20px] accent-[#FF703E] cursor-pointer"
                    />
                    <label
                      htmlFor="cancel-bill-confirm-details"
                      className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                    >
                      Confirm all details
                    </label>
                  </div>
                  {cancelRowError && (
                    <div
                      role="alert"
                      aria-live="assertive"
                      className="mb-4 p-3 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                    >
                      {cancelRowError}
                    </div>
                  )}
                  <div className="flex gap-4">
                    <button
                      type="button"
                      disabled={submitting || !cancelRowConfirmed}
                      onClick={() => void confirmCancelRow(cancelRow)}
                      aria-label={`Confirm cancel ${cancelRow.Name ?? 'bill payment'}`}
                      className="px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Working…' : 'Confirm cancellation'}
                    </button>
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => {
                        setCancelRow(null);
                        setCancelRowConfirmed(false);
                        setCancelRowError('');
                      }}
                      className="px-6 py-2.5 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#f5f5f5] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Keep bill payment
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Beneficiary detail panel — shows on row-click (CCF-66). */}
            {selectedPaymentId !== null && (
              <div className="mt-8 max-w-[900px] border-2 border-[#dad7d3] bg-white p-8">
                <h2 className="text-[24px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-8">
                  View/edit/cancel bill payment
                </h2>

                {detailLoading ? (
                  <p
                    role="status"
                    className="text-[16px] font-['Arial',sans-serif] text-[#6b7280]"
                  >
                    Loading details…
                  </p>
                ) : detail ? (
                  <>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                      {/* Beneficiary IBAN — read-only (CCF-66). */}
                      <div>
                        <label className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2">
                          Beneficiary IBAN
                        </label>
                        <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                          {detail.BeneficiaryIban ?? '—'}
                        </p>
                      </div>
                      <div></div>

                      {/* Beneficiary BIC — read-only (CCF-66). */}
                      <div>
                        <label className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2">
                          Beneficiary BIC
                        </label>
                        <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                          {detail.BeneficiaryBic ?? '—'}
                        </p>
                      </div>
                      <div></div>

                      {/* Beneficiary name — editable, max 18 (CCF-66), EXCEPT
                          for utility/VISA/mortgage-type billers whose name the
                          gateway refuses to amend (9287) — greyed out + a hint
                          so the agent can't attempt it (#530). #658 (CCRS-3427):
                          it is ALSO greyed out / read-only for a non-active
                          (cancelled/lapsed/completed) payment, mirroring the
                          reference + remit fields (#579). */}
                      <div>
                        <label
                          htmlFor="amend-bill-beneficiary-name"
                          className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                        >
                          Beneficiary name
                        </label>
                        <input
                          type="text"
                          id="amend-bill-beneficiary-name"
                          value={editName}
                          onChange={(e) => {
                            setEditName(e.target.value);
                            setNameError('');
                          }}
                          maxLength={18}
                          disabled={!nameEditable}
                          aria-invalid={!!nameError}
                          aria-describedby={
                            detailNameLocked
                              ? 'amend-bill-beneficiary-name-locked'
                              : undefined
                          }
                          className={`w-full px-4 py-2 border rounded text-[14px] font-['Arial',sans-serif] placeholder:text-[#9ca3af] focus:outline-none ${
                            !nameEditable
                              ? 'bg-[#f3f4f6] text-[#6b7280] border-[#d1d5db] cursor-not-allowed'
                              : `text-[#1d252c] focus:ring-2 focus:ring-[#005fcc] focus:border-[#005fcc] ${
                                  nameError
                                    ? 'border-[#dc2626]'
                                    : 'border-[#979c9f]'
                                }`
                          }`}
                        />
                        {detailNameLocked && (
                          <p
                            id="amend-bill-beneficiary-name-locked"
                            className="text-[13px] font-['Arial',sans-serif] text-[#6b7280] mt-1 mb-0"
                          >
                            The beneficiary name cannot be amended for this bill
                            type.
                          </p>
                        )}
                        <div className="min-h-[20px]">
                          {nameError && (
                            <p
                              className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                              role="alert"
                            >
                              {nameError}
                            </p>
                          )}
                        </div>
                      </div>
                      <div></div>

                      {/* Beneficiary reference — editable for active payments,
                          greyed-out/read-only for cancelled/lapsed/completed
                          (#579 follow-up). Max 18 characters (CCF-66). */}
                      <div>
                        <label
                          htmlFor="amend-bill-beneficiary-reference"
                          className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                        >
                          Beneficiary reference, max 18 characters
                        </label>
                        <input
                          type="text"
                          id="amend-bill-beneficiary-reference"
                          value={editRef}
                          onChange={(e) => {
                            setEditRef(e.target.value);
                            setRefError('');
                          }}
                          maxLength={18}
                          disabled={!detailIsActive}
                          aria-invalid={!!refError}
                          className={`w-full px-4 py-2 border rounded text-[14px] font-['Arial',sans-serif] placeholder:text-[#9ca3af] focus:outline-none ${
                            !detailIsActive
                              ? 'bg-[#f3f4f6] text-[#6b7280] border-[#d1d5db] cursor-not-allowed'
                              : `text-[#1d252c] focus:ring-2 focus:ring-[#005fcc] focus:border-[#005fcc] ${
                                  refError
                                    ? 'border-[#dc2626]'
                                    : 'border-[#979c9f]'
                                }`
                          }`}
                        />
                        <div className="min-h-[20px]">
                          {refError && (
                            <p
                              className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                              role="alert"
                            >
                              {refError}
                            </p>
                          )}
                        </div>
                      </div>
                      <div></div>
                    </div>

                    {/* Remit data — editable for active payments, greyed-out/
                        read-only for cancelled/lapsed/completed (#579 follow-up,
                        CCF-66). */}
                    <div className="mt-6 grid grid-cols-2 gap-x-8">
                      <div>
                        <label
                          htmlFor="amend-bill-remit-data"
                          className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                        >
                          Remit data
                        </label>
                        <textarea
                          id="amend-bill-remit-data"
                          value={editRemit}
                          onChange={(e) => {
                            setEditRemit(e.target.value);
                            setRemitError('');
                          }}
                          maxLength={40}
                          rows={5}
                          disabled={!detailIsActive}
                          aria-invalid={!!remitError}
                          className={`w-full px-4 py-2 border rounded text-[14px] font-['Arial',sans-serif] placeholder:text-[#9ca3af] focus:outline-none resize-none ${
                            !detailIsActive
                              ? 'bg-[#f3f4f6] text-[#6b7280] border-[#d1d5db] cursor-not-allowed'
                              : `text-[#1d252c] focus:ring-2 focus:ring-[#005fcc] focus:border-[#005fcc] ${
                                  remitError
                                    ? 'border-[#dc2626]'
                                    : 'border-[#979c9f]'
                                }`
                          }`}
                        />
                        <div className="min-h-[20px]">
                          {remitError && (
                            <p
                              className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                              role="alert"
                            >
                              {remitError}
                            </p>
                          )}
                        </div>
                      </div>
                      <div></div>
                    </div>

                    {/* Confirm-all-details checkbox gates Amend/Cancel (CCF-64).
                        #658 (CCRS-3427): shown ONLY for an ACTIVE payment — a
                        cancelled/lapsed/completed payment cannot be amended or
                        cancelled, so there is nothing for the agent to confirm. */}
                    {detailIsActive && (
                      <div className="mt-6 flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="amend-bill-confirm-details"
                          checked={confirmDetails}
                          onChange={() => setConfirmDetails((c) => !c)}
                          className="size-[20px] accent-[#FF703E] cursor-pointer"
                        />
                        <label
                          htmlFor="amend-bill-confirm-details"
                          className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                        >
                          Confirm all details
                        </label>
                      </div>
                    )}

                    {/* Amend (CCF-67) / Cancel (CCF-68) — only when details are
                        confirmed (CCF-64). The listing is refreshed on success.
                        #579 follow-up: Amend is also hidden for non-active
                        payments (cancelled/lapsed/completed) — the fields are
                        already greyed-out/read-only, so there is nothing to
                        amend. #658 (CCRS-3427): the "Confirm all details"
                        checkbox is now hidden for non-active payments too, so
                        confirmDetails stays false and both buttons remain
                        suppressed. */}
                    {confirmDetails && (
                      <div className="mt-6 flex flex-col gap-3">
                        {/* #799 increment 3 — verbatim inline advisory beside the
                            action row when the account requires >1 signatory (the
                            Amend control is suppressed above). */}
                        {accountBlocksAmend && (
                          <div
                            role="alert"
                            aria-live="polite"
                            className="max-w-[640px] p-3 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                          >
                            {MULTIPLE_SIGNATORIES_MESSAGE}
                          </div>
                        )}
                        <div className="flex gap-4">
                          {/* #579 follow-up: Amend shown ONLY for active
                              payments — the beneficiary reference + remit data
                              fields are read-only for cancelled ones, so there
                              is nothing to submit. Mirrors the Cancel gate. */}
                          {/* #799 increment 3 (@dylan219): amending a payee is
                              NOT permitted on a >1-signatory ("two to sign")
                              account — the control is suppressed entirely and
                              the verbatim block message shows inline beside the
                              action below. The BFF /payments/bill-amend route
                              enforces the SAME block server-side (#173), so a
                              reload / direct call cannot bypass it. Bill
                              payment CANCELLATION is unaffected (not one of the
                              six blocked action-areas). */}
                          {detailIsActive && !accountBlocksAmend && (
                            <button
                              type="button"
                              disabled={submitting}
                              onClick={() => void amendPayment()}
                              aria-describedby={
                                actionError ? 'bill-action-error' : undefined
                              }
                              className="px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {submitting ? 'Working…' : 'Amend bill payment'}
                            </button>
                          )}
                          {/* #579 (CCF-68): the Cancel action is shown ONLY for
                              an ACTIVE payment — an already cancelled/lapsed/
                              completed payment cannot be cancelled again, so the
                              button is omitted. */}
                          {isBillPaymentActive(detail?.Status) && (
                            <button
                              type="button"
                              disabled={submitting}
                              onClick={() => void cancelPayment()}
                              aria-describedby={
                                actionError ? 'bill-action-error' : undefined
                              }
                              className="px-6 py-2.5 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#f5f5f5] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Cancel bill payment
                            </button>
                          )}
                        </div>
                        {/* Inline, non-blocking, retryable gateway Code + Message
                            beside the action buttons (authoritative @dylan219,
                            #87) — the form stays open so the agent can retry. */}
                        {actionError && (
                          <div
                            id="bill-action-error"
                            role="alert"
                            aria-live="assertive"
                            className="max-w-[640px] p-3 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                          >
                            {actionError}
                          </div>
                        )}
                      </div>
                    )}

                    {/* The SEPA CT Recall email-template pack (#254) formerly
                        rendered here has been consolidated into the single
                        top-right EmailTemplateMenu in the page header (#442,
                        mirroring #441). */}
                  </>
                ) : (
                  <p
                    role="alert"
                    className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]"
                  >
                    Could not load bill payment details. Please retry.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
    </main>
  );
}
